import React, { useState, useEffect, useRef } from 'react';

const AuditReadyIQ = () => {
  const [currentStep, setCurrentStep] = useState('intro');
  const [currentCategory, setCurrentCategory] = useState(0);
  const [answers, setAnswers] = useState({});
  const [businessInfo, setBusinessInfo] = useState({ name: '', industry: '', size: '', email: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('onetime');
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(29.99);
  const [activeTab, setActiveTab] = useState('business');
  const [reportSaved, setReportSaved] = useState(false);
  const [showSaveReminder, setShowSaveReminder] = useState(false);
  const paypalRef = useRef(null);
  const [hasStoredReport, setHasStoredReport] = useState(false);
  const [printPrompted, setPrintPrompted] = useState(false);

  // Check for stored report on initial load
  useEffect(() => {
    const storedReport = localStorage.getItem('auditreadyiq_report');
    const storedBusiness = localStorage.getItem('auditreadyiq_business');
    if (storedReport && storedBusiness) {
      try {
        const parsedReport = JSON.parse(storedReport);
        const parsedBusiness = JSON.parse(storedBusiness);
        setReport(parsedReport);
        setBusinessInfo(parsedBusiness);
        setPaymentComplete(true);
        setHasStoredReport(true);
        console.log('Found stored report for:', parsedBusiness.name);
      } catch (e) {
        console.log('Error parsing stored report');
        localStorage.removeItem('auditreadyiq_report');
        localStorage.removeItem('auditreadyiq_business');
      }
    }
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Auto-prompt print dialog when report loads (first time only)
  useEffect(() => {
    if (currentStep === 'report' && report && !printPrompted) {
      setPrintPrompted(true);
      setTimeout(() => {
        const shouldPrint = window.confirm(
          '📄 Save your report as PDF?\n\nClick OK to open the print dialog (choose "Save as PDF" as your printer).\n\nClick Cancel to view the report first.'
        );
        if (shouldPrint) {
          const downloadBtn = document.querySelector('[data-download-pdf]');
          if (downloadBtn) downloadBtn.click();
        }
      }, 500);
    }
  }, [currentStep, report, printPrompted]);

  // Save report to localStorage when generated
  useEffect(() => {
    if (report && businessInfo.email && currentStep === 'report') {
      localStorage.setItem('auditreadyiq_report', JSON.stringify(report));
      localStorage.setItem('auditreadyiq_business', JSON.stringify(businessInfo));
      console.log('Report saved to localStorage');
    }
  }, [report, businessInfo, currentStep]);

  // Warn before leaving if report not saved
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (currentStep === 'report' && !reportSaved) {
        e.preventDefault();
        e.returnValue = 'Your report may not be saved. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep, reportSaved]);

  // AuditReadyIQ Brand Colors - Green theme
  const colors = {
    green: '#10b981',
    greenLight: 'rgba(16, 185, 129, 0.15)',
    greenBorder: 'rgba(16, 185, 129, 0.4)',
    black: '#1a1a1a',
    darkBg: '#0d0d0d',
    darkCard: '#161616',
    darkCardHover: '#1f1f1f',
    white: '#ffffff',
    gray: '#a3a3a3',
    grayLight: '#d4d4d4',
    grayDark: '#525252'
  };

  // Sanitize user input to prevent XSS
  const escapeHtml = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // Simple markdown to HTML converter
  const markdownToHtml = (markdown) => {
    if (!markdown) return '';
    
    let html = markdown
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/^### (.*$)/gm, '<h3 style="color: #ffffff; font-size: 1.1rem; margin: 1.5rem 0 0.75rem; font-weight: 600;">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 style="color: #10b981; font-size: 1.3rem; margin: 2rem 0 1rem; font-weight: 700; border-bottom: 1px solid #333; padding-bottom: 0.5rem;">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 style="color: #ffffff; font-size: 2rem; margin: 1rem 0; font-weight: 700;">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #ffffff;">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^---$/gm, '<hr style="border: none; border-top: 1px solid #333; margin: 1.5rem 0;">')
      .replace(/^- (.*$)/gm, '<li style="margin: 0.5rem 0; margin-left: 1.5rem;">$1</li>')
      .replace(/\|:?-+:?\|/g, '')
      .replace(/^\|(.*)\|$/gm, (match, content) => {
        const cells = content.split('|').map(cell => cell.trim());
        return '<tr>' + cells.map(cell => `<td style="padding: 0.5rem; border: 1px solid #333;">${cell}</td>`).join('') + '</tr>';
      })
      .replace(/\n\n/g, '</p><p style="margin: 1rem 0; line-height: 1.7;">')
      .replace(/\n/g, '<br>');
    
    html = '<p style="margin: 1rem 0; line-height: 1.7;">' + html + '</p>';
    html = html.replace(/<p[^>]*><\/p>/g, '');
    html = html.replace(/<p[^>]*><br><\/p>/g, '');
    
    return html;
  };

  // Scroll to top whenever step or category changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }, [currentStep, currentCategory, showPaywall]);

  const [paypalLoading, setPaypalLoading] = useState(false);
  const [paypalSdkType, setPaypalSdkType] = useState(null);

  const neededSdkType = selectedPlan === 'onetime' ? 'onetime' : 'subscription';

  // Load PayPal SDK and render button when paywall opens
  useEffect(() => {
    if (showPaywall && !paymentComplete && businessInfo.email && businessInfo.email.includes('@')) {
      
      if (paypalSdkType !== neededSdkType) {
        setPaypalLoading(true);
        
        const existingScripts = document.querySelectorAll('script[src*="paypal.com/sdk"]');
        existingScripts.forEach(s => s.remove());
        
        if (window.paypal) {
          delete window.paypal;
        }
        
        const container = paypalRef.current;
        if (container) {
          container.innerHTML = '';
        }
        
        const script = document.createElement('script');
        const clientId = 'ATtOAGgoUaBRiQSclDhG6I7ER_KhNPgWGs3WUJYgs1fIUw4htpDW0d8NRCzehPkLxTNNBorisya_-NaK';
        script.src = selectedPlan === 'onetime' 
          ? `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`
          : `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
        
        script.onload = () => {
          setPaypalSdkType(neededSdkType);
          setPaypalLoading(false);
          
          setTimeout(() => {
            const container = paypalRef.current;
            if (container && window.paypal) {
              container.innerHTML = '';
              
              if (selectedPlan === 'onetime') {
                const price = appliedDiscount === 100 ? 0 : discountedPrice;
                
                if (price === 0) {
                  setPaymentComplete(true);
                  generateReport();
                  return;
                }
                
                window.paypal.Buttons({
                  style: {
                    layout: 'vertical',
                    color: 'gold',
                    shape: 'pill',
                    label: 'paypal',
                    height: 45
                  },
                  createOrder: (data, actions) => {
                    return actions.order.create({
                      purchase_units: [{
                        amount: { value: price.toFixed(2) },
                        description: 'AuditReadyIQ™ - Documentation Readiness Report'
                      }]
                    });
                  },
                  onApprove: async (data, actions) => {
                    await actions.order.capture();
                    setPaymentComplete(true);
                    generateReport();
                  },
                  onError: (err) => {
                    console.error('PayPal Error:', err);
                    alert('Payment failed. Please try again.');
                  }
                }).render(container);
              }
            }
          }, 100);
        };
        
        script.onerror = () => {
          console.error('Failed to load PayPal SDK');
          setPaypalLoading(false);
        };
        
        document.body.appendChild(script);
      } else if (window.paypal && paypalRef.current) {
        paypalRef.current.innerHTML = '';
        
        if (selectedPlan === 'onetime') {
          const price = appliedDiscount === 100 ? 0 : discountedPrice;
          
          if (price === 0) {
            return;
          }
          
          window.paypal.Buttons({
            style: {
              layout: 'vertical',
              color: 'gold',
              shape: 'pill',
              label: 'paypal',
              height: 45
            },
            createOrder: (data, actions) => {
              return actions.order.create({
                purchase_units: [{
                  amount: { value: price.toFixed(2) },
                  description: 'AuditReadyIQ™ - Documentation Readiness Report'
                }]
              });
            },
            onApprove: async (data, actions) => {
              await actions.order.capture();
              setPaymentComplete(true);
              generateReport();
            },
            onError: (err) => {
              console.error('PayPal Error:', err);
              alert('Payment failed. Please try again.');
            }
          }).render(paypalRef.current);
        }
      }
    }
  }, [showPaywall, paymentComplete, businessInfo.email, selectedPlan, neededSdkType, paypalSdkType, appliedDiscount, discountedPrice]);

  // Industry options (detailed like BreachBlockIQ)
  const industries = [
    'Healthcare / Medical',
    'Financial Services',
    'Professional Services (Legal, Accounting, Consulting)',
    'Religious Organization (Church, Synagogue, Mosque, Temple)',
    'Education (Public School, District, University)',
    'Private School / Academy',
    'Nonprofit / Association',
    'Retail / E-commerce',
    'Construction (GCs, Builders, Developers)',
    'Manufacturing / Warehouse',
    'Restaurant / Hospitality',
    'Real Estate',
    'IT / Technology Services (Managed Services, Computer Repair, Web/Software)',
    'Daycare / Childcare Center',
    'Funeral Services (Funeral Home, Crematory, Cemetery)',
    'Personal Services (Salon, Spa, Fitness, Pet Care)',
    'Trades & Repair Services (HVAC, Plumbing, Auto, Electrical)',
    'Other'
  ];

  // 25 Audit-Specific Questions across 5 Categories
  const categories = [
    {
      id: 'document_retention',
      title: 'Document Retention',
      icon: '📁',
      questions: [
        { 
          id: 'retention_policy', 
          text: 'Does your organization have a written document retention policy?', 
          helper: 'A retention policy defines how long you keep different types of records. This helps with legal compliance, reduces storage costs, and protects you during audits or litigation.',
          options: ['Yes, formal written policy', 'Informal guidelines exist', 'No written policy', 'Not sure'] 
        },
        { 
          id: 'retention_schedule', 
          text: 'Do you have a retention schedule that specifies how long to keep each type of document?', 
          helper: 'Different documents have different legal retention requirements. Tax records (typically 7 years), employee files (varies by state), contracts, etc. A schedule prevents premature destruction or unnecessary hoarding.',
          options: ['Yes, detailed schedule by document type', 'General timeframes only', 'No schedule', 'Not sure'] 
        },
        { 
          id: 'destruction_process', 
          text: 'Do you have a secure process for destroying documents when retention periods expire?', 
          helper: 'Secure destruction prevents sensitive data from being recovered. Shredding, certified destruction services, or secure digital wiping are standard methods.',
          options: ['Yes, scheduled secure destruction', 'Ad-hoc shredding/deletion', 'Documents accumulate indefinitely', 'Not sure'] 
        },
        { 
          id: 'legal_hold', 
          text: 'Do you have a process to suspend document destruction when litigation is anticipated (legal hold)?', 
          helper: 'Once litigation is reasonably anticipated, destroying relevant documents can result in severe legal penalties. A legal hold process ensures potentially relevant documents are preserved.',
          options: ['Yes, formal legal hold process', 'Would figure it out if needed', 'No process', 'Not sure what this means'] 
        },
        { 
          id: 'electronic_records', 
          text: 'Are electronic records (emails, digital files) included in your retention policy?', 
          helper: 'Digital records are subject to the same legal requirements as paper. Email, chat logs, cloud files, and databases all need retention management.',
          options: ['Yes, all digital records covered', 'Some digital records included', 'Paper only', 'Not sure'] 
        }
      ]
    },
    {
      id: 'file_organization',
      title: 'File Organization',
      icon: '🗂️',
      questions: [
        { 
          id: 'filing_system', 
          text: 'Do you have a consistent filing system that all staff follow?', 
          helper: 'Consistent naming conventions and folder structures mean anyone can find documents quickly, even if the person who created them is unavailable.',
          options: ['Yes, standardized system enforced', 'Guidelines exist but inconsistent', 'Everyone has their own system', 'Not sure'] 
        },
        { 
          id: 'retrieval_time', 
          text: 'If an auditor requested a specific document, how quickly could you produce it?', 
          helper: 'Auditors expect timely document production. Delays suggest disorganization and can raise red flags. Best practice: any document within 24-48 hours.',
          options: ['Within hours', 'Within 1-2 days', 'Days to weeks', 'Not confident we could find it'] 
        },
        { 
          id: 'personnel_files', 
          text: 'Are employee personnel files stored separately from payroll and medical records?', 
          helper: 'Best practice separates: (1) general personnel files, (2) payroll/tax documents, (3) medical/FMLA (Family and Medical Leave Act) records, and (4) I-9 forms. This protects privacy and limits access.',
          options: ['Yes, properly separated', 'Partially separated', 'All together', 'Not sure'] 
        },
        { 
          id: 'i9_storage', 
          text: 'Are I-9 employment eligibility forms stored separately and accessible for government inspection?', 
          helper: 'I-9 forms verify employment eligibility for all US employees. ICE (Immigration and Customs Enforcement) can request these with just 3 days notice. They must be stored separately from personnel files.',
          options: ['Yes, separate and organized', 'Mixed with other HR files', 'Not sure where they are', 'We may be missing some'] 
        },
        { 
          id: 'backup_documentation', 
          text: 'Do you keep backup copies of critical documents in a separate location?', 
          helper: 'Fire, flood, or theft can destroy your only copies. Critical documents should have backups stored offsite or in secure cloud storage.',
          options: ['Yes, offsite/cloud backup', 'Some documents backed up', 'No backup copies', 'Not sure'] 
        }
      ]
    },
    {
      id: 'record_management',
      title: 'Record Accuracy',
      icon: '📝',
      questions: [
        { 
          id: 'timekeeping', 
          text: 'Do you maintain accurate timekeeping records for all non-exempt (hourly) employees?', 
          helper: 'The FLSA (Fair Labor Standards Act) requires employers to keep time records for non-exempt employees. These records prove compliance with overtime and minimum wage laws.',
          options: ['Yes, automated time tracking', 'Manual but consistent', 'Informal or inconsistent', 'Not sure about our system'] 
        },
        { 
          id: 'pay_records', 
          text: 'Do you retain pay stubs and payroll records for the required period (typically 3+ years)?', 
          helper: 'Federal law requires 3 years for basic pay records, but state laws vary. Some require up to 6 years. These prove you paid employees correctly.',
          options: ['Yes, 3+ years retained', 'Keep 1-2 years', 'Not sure how long we keep them', 'Records are incomplete'] 
        },
        { 
          id: 'contract_management', 
          text: 'Are all signed contracts, agreements, and amendments properly filed and accessible?', 
          helper: 'Contracts define your legal obligations. Missing contracts can mean you cannot prove terms, leading to disputes or unfavorable assumptions.',
          options: ['Yes, centralized contract system', 'Most contracts filed', 'Scattered across locations', 'Not sure'] 
        },
        { 
          id: 'version_control', 
          text: 'Do you maintain version control for policies and handbooks (tracking what was in effect when)?', 
          helper: 'If an employee claims they were not informed of a policy, you need to prove which version was in effect and when they acknowledged it.',
          options: ['Yes, dated versions archived', 'Keep current version only', 'No version tracking', 'Not sure'] 
        },
        { 
          id: 'audit_trail', 
          text: 'Do your financial records have a clear audit trail (who entered data, when, what changed)?', 
          helper: 'An audit trail shows who made changes and when. This prevents fraud, catches errors, and satisfies auditor requirements for internal controls.',
          options: ['Yes, system logs all changes', 'Partial tracking', 'No audit trail', 'Not sure'] 
        }
      ]
    },
    {
      id: 'compliance_documentation',
      title: 'Compliance Records',
      icon: '✅',
      questions: [
        { 
          id: 'labor_posters', 
          text: 'Are required federal and state labor law posters displayed and current?', 
          helper: 'Employers must display posters about minimum wage, OSHA (Occupational Safety and Health Administration), FMLA, EEO (Equal Employment Opportunity), and more. Fines for missing posters can reach $35,000+.',
          options: ['Yes, current and displayed', 'Have posters but may be outdated', 'Missing some required posters', 'Not sure'] 
        },
        { 
          id: 'training_records', 
          text: 'Do you document required employee training (safety, harassment, job-specific)?', 
          helper: 'If an incident occurs, you need to prove employees were trained. This includes OSHA safety, harassment prevention (required in many states), and industry-specific training.',
          options: ['Yes, tracked with completion dates', 'Some training documented', 'Training not consistently tracked', 'Not sure'] 
        },
        { 
          id: 'policy_acknowledgments', 
          text: 'Do employees sign acknowledgments for the employee handbook and key policies?', 
          helper: 'Signed acknowledgments prove employees received and agreed to follow your policies. Without them, you cannot hold employees accountable or defend against claims.',
          options: ['Yes, for handbook and all key policies', 'Handbook only', 'No formal acknowledgments', 'Not sure'] 
        },
        { 
          id: 'osha_logs', 
          text: 'Do you maintain OSHA injury and illness logs if required (most employers with 10+ employees)?', 
          helper: 'OSHA (Occupational Safety and Health Administration) requires most employers to log work-related injuries and illnesses. Form 300 logs must be kept for 5 years.',
          options: ['Yes, current OSHA 300 logs', 'Maintain but may be incomplete', 'Not sure if required', 'Do not maintain logs'] 
        },
        { 
          id: 'license_tracking', 
          text: 'Do you track expiration dates for business licenses, certifications, and professional credentials?', 
          helper: 'Operating with expired licenses can result in fines, loss of contracts, or business closure. Employee credentials (RN, CPA, CDL) may also require tracking.',
          options: ['Yes, with renewal reminders', 'Track some but not all', 'No tracking system', 'Not sure'] 
        }
      ]
    },
    {
      id: 'access_security',
      title: 'Access & Security',
      icon: '🔒',
      questions: [
        { 
          id: 'file_access', 
          text: 'Is access to sensitive files (personnel, financial, medical) restricted to authorized personnel only?', 
          helper: 'Not everyone should access everything. Limiting access protects privacy, reduces fraud risk, and is often required by regulations like HIPAA.',
          options: ['Yes, role-based access controls', 'Somewhat limited', 'Most staff can access most files', 'Not sure'] 
        },
        { 
          id: 'physical_security', 
          text: 'Are physical file storage areas secured (locked cabinets, restricted rooms)?', 
          helper: 'Paper files with sensitive information should be in locked cabinets or rooms with limited key access. This prevents theft and unauthorized viewing.',
          options: ['Yes, locked and restricted', 'Partially secured', 'Open access', 'Not applicable (all digital)'] 
        },
        { 
          id: 'digital_security', 
          text: 'Are digital files protected with appropriate security (passwords, encryption, access logs)?', 
          helper: 'Digital files need password protection at minimum. Sensitive data should be encrypted. Access logs show who viewed or changed files.',
          options: ['Yes, comprehensive digital security', 'Basic password protection', 'Minimal security', 'Not sure'] 
        },
        { 
          id: 'offboarding_access', 
          text: 'When employees leave, is their access to files and systems promptly removed?', 
          helper: 'Former employees with lingering access pose security and liability risks. Access should be removed on or before their last day.',
          options: ['Yes, same-day removal process', 'Within a few days', 'No formal process', 'Not sure'] 
        },
        { 
          id: 'confidentiality_agreements', 
          text: 'Do employees handling sensitive information sign confidentiality or NDA (Non-Disclosure Agreement) agreements?', 
          helper: 'NDAs create legal accountability for protecting confidential information. They should be signed at hire for roles with access to sensitive data.',
          options: ['Yes, required for relevant roles', 'Sometimes', 'No', 'Not sure'] 
        }
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const calculateRiskScore = () => {
    let highRisk = 0;
    let mediumRisk = 0;
    let iqScore = 0;
    
    Object.entries(answers).forEach(([key, value]) => {
      if (value.includes('No ') || value.includes('No written') || value.includes('No formal') || value.includes('No tracking') || value.includes('No schedule') || value.includes('No process') || value.includes('No version') || value.includes('No audit') || value === 'Not sure' || value.includes('Not sure what') || value.includes('Not sure where') || value.includes('Not sure about') || value.includes('Not sure if') || value.includes('Not confident') || value.includes('Do not maintain') || value.includes('may be missing') || value.includes('Records are incomplete') || value.includes('accumulate indefinitely') || value.includes('Missing some') || value.includes('Minimal security') || value.includes('Open access')) {
        highRisk++;
        iqScore += 0;
      } else if (value.includes('Informal') || value.includes('Partial') || value.includes('Some ') || value.includes('Sometimes') || value.includes('inconsistent') || value.includes('General timeframes') || value.includes('Ad-hoc') || value.includes('Would figure') || value.includes('Guidelines exist but') || value.includes('Days to weeks') || value.includes('Mixed with') || value.includes('Manual but') || value.includes('Keep 1-2') || value.includes('Scattered') || value.includes('Keep current version') || value.includes('may be outdated') || value.includes('not consistently') || value.includes('Handbook only') || value.includes('Maintain but') || value.includes('Track some') || value.includes('Somewhat limited') || value.includes('Basic password') || value.includes('Within a few days')) {
        mediumRisk++;
        iqScore += 12;
      } else {
        iqScore += 20;
      }
    });
    
    return { 
      highRisk, 
      mediumRisk, 
      lowRisk: Object.keys(answers).length - highRisk - mediumRisk,
      iqScore: iqScore
    };
  };

  // Send report email function
  const sendReportEmail = async (emailAddress, reportData) => {
    console.log('sendReportEmail called with:', emailAddress);
    
    if (!emailAddress || !emailAddress.includes('@')) {
      console.log('No valid email provided for report delivery');
      return;
    }
    
    try {
      if (typeof window.emailjs === 'undefined') {
        console.error('EmailJS not loaded - email will not be sent');
        return;
      }
      
      console.log('EmailJS is loaded, preparing to send to:', emailAddress);
      
      const r = reportData || {};
      const { iqScore } = calculateRiskScore();
      
      let prioritiesText = '';
      if (r.priorities && r.priorities.length > 0) {
        prioritiesText = r.priorities.map((p, i) => 
          `${i + 1}. ${p.title || 'Priority'}\n   → ${p.reason || ''}`
        ).join('\n\n');
      }
      
      let criticalText = '';
      if (r.criticalIssues && r.criticalIssues.length > 0) {
        criticalText = r.criticalIssues.map(issue => 
          `⚠️ ${issue.topic || 'Issue'}\n   Risk: ${issue.risk || 'See recommendation'}\n   Fix: ${issue.fix || 'Address this issue'}\n   Effort: ${issue.effort || 'Varies'}`
        ).join('\n\n');
      }
      
      let attentionText = '';
      if (r.attentionIssues && r.attentionIssues.length > 0) {
        attentionText = r.attentionIssues.map(issue => 
          `• ${issue.topic || 'Issue'}\n   Fix: ${issue.fix || 'Address this'}\n   Effort: ${issue.effort || 'Varies'}`
        ).join('\n\n');
      }
      
      let goodText = '';
      if (r.goodPractices && r.goodPractices.length > 0) {
        goodText = r.goodPractices.map(practice => `✓ ${practice}`).join('\n');
      }
      
      let actionText = '';
      if (r.actionPlan) {
        const week1 = r.actionPlan.week1 ? r.actionPlan.week1.join('\n   • ') : '';
        const week2 = r.actionPlan.week2to4 ? r.actionPlan.week2to4.join('\n   • ') : '';
        const ongoing = r.actionPlan.ongoing ? r.actionPlan.ongoing.join('\n   • ') : '';
        actionText = `WEEK 1 (Quick Wins):\n   • ${week1}\n\nWEEK 2-4:\n   • ${week2}\n\nONGOING:\n   • ${ongoing}`;
      }
      
      const fullReport = `
═══════════════════════════════════════
       YOUR AUDITREADYIQ™ REPORT
═══════════════════════════════════════

Organization: ${businessInfo.name || 'Your Organization'}
Industry: ${businessInfo.industry || 'General'}
Date: ${new Date().toLocaleDateString()}

───────────────────────────────────────
SCORE: ${r.score || iqScore}/500 - ${r.riskLevel || 'PENDING'}
───────────────────────────────────────

Critical Issues: ${r.criticalCount || 0}
Needs Attention: ${r.attentionCount || 0}
Good Practices: ${r.goodCount || 0}

═══════════════════════════════════════
EXECUTIVE SUMMARY
═══════════════════════════════════════

${r.executiveSummary || 'Assessment completed.'}

═══════════════════════════════════════
TOP 3 PRIORITIES
═══════════════════════════════════════

${prioritiesText || 'See full report for priorities.'}

═══════════════════════════════════════
CRITICAL ISSUES (${r.criticalCount || 0})
═══════════════════════════════════════

${criticalText || 'No critical issues identified.'}

═══════════════════════════════════════
NEEDS ATTENTION (${r.attentionCount || 0})
═══════════════════════════════════════

${attentionText || 'No attention items identified.'}

═══════════════════════════════════════
WHAT YOU'RE DOING WELL (${r.goodCount || 0})
═══════════════════════════════════════

${goodText || 'See full report.'}

═══════════════════════════════════════
30-DAY ACTION PLAN
═══════════════════════════════════════

${actionText || 'See full report for action plan.'}

───────────────────────────────────────

This is an educational tool providing guidance based on your self-reported answers. It is not a professional audit, compliance certification, or legal advice.

Need help implementing? Contact TechShield KC:
www.techshieldkc.com | info@techshieldkc.com

© ${new Date().getFullYear()} AuditReadyIQ™ | TechShield KC LLC
`;

      const templateParams = {
        to_email: emailAddress,
        to_name: businessInfo.name || 'Customer',
        from_name: 'AuditReadyIQ',
        iq_score: r.score || iqScore,
        risk_level: r.riskLevel || 'PENDING',
        critical_issues: r.criticalCount || 0,
        attention_items: r.attentionCount || 0,
        good_practices: r.goodCount || 0,
        industry: businessInfo.industry || 'General',
        organization: businessInfo.name || 'Your Organization',
        report_summary: r.executiveSummary || 'Your audit readiness assessment is complete.',
        full_report: fullReport
      };
      
      console.log('Sending email with full report...');
      
      const result = await window.emailjs.send(
        'service_9hdzso6',
        'template_n2flukf',
        templateParams
      );
      
      console.log('Email sent successfully:', result);
    } catch (error) {
      console.error('Failed to send report email:', error);
    }
  };

  // Background report generation
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [pendingReport, setPendingReport] = useState(null);

  const generateReport = async () => {
    if (reportReady && pendingReport) {
      setLoading(false);
      setReport(pendingReport);
      setCurrentStep('report');
      
      if (businessInfo.email) {
        sendReportEmail(businessInfo.email, pendingReport);
      }
      return;
    }
    
    setLoading(true);
    setShowPaywall(false);
    
    if (!reportGenerating) {
      await generateReportAPI();
    }
  };

  const generateReportAPI = async () => {
    setReportGenerating(true);
    
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const prompt = `You are an audit readiness advisor. Analyze this document management assessment and return ONLY valid JSON.

BUSINESS: ${businessInfo.name}
INDUSTRY: ${businessInfo.industry}
SIZE: ${businessInfo.size}

THEIR ANSWERS:
${Object.entries(answers).map(([q, a]) => `${q}: ${a}`).join('\n')}

SCORING RULES:
- 25 questions × 20 points each = 500 max
- Best practice answer = 20 points
- Partial/some = 12 points
- No protection/poor practice = 0 points
- Score ranges: Under 200=HIGH RISK, 200-300=ELEVATED RISK, 300-400=MODERATE, 400-500=STRONG

INDUSTRY-SPECIFIC CONTEXT (use audit/documentation concerns relevant to their industry):
- Religious Organizations: Donor record requirements, IRS 501(c)(3) documentation, volunteer background check records, benevolence fund disbursement documentation, contribution statements.
- Daycare / Childcare: State licensing documentation, staff-to-child ratio logs, incident reports, parent authorization forms, immunization records, background check retention.
- Private School: Student records (FERPA compliance), faculty credentials, accreditation documentation, tuition agreements, financial aid records.
- Nonprofit / Association: IRS Form 990 supporting documentation, grant compliance records, donor restricted fund tracking, board meeting minutes, conflict of interest disclosures.
- Healthcare / Medical: HIPAA documentation requirements, medical records retention (typically 7+ years), credentialing files, patient consent forms, billing and coding records.
- Financial Services: SEC/FINRA recordkeeping requirements, client suitability documentation, trading records, compliance certifications, client communication archives.
- Legal Services: Client file retention (varies by state, often 7+ years), conflict check documentation, billing records, trust account records, engagement letters.
- Retail / E-commerce: Sales tax records, inventory documentation, vendor agreements, PCI compliance records if accepting cards, return/refund documentation.
- Professional Services: Engagement letters, deliverable documentation, time/billing records, professional liability documentation, subcontractor agreements.
- IT / Technology Services: Service level agreements, change management logs, incident documentation, client data handling procedures, SOC compliance documentation.
- Education: Student records (FERPA), faculty certifications, Title IX documentation, financial aid records, accreditation files.
- Construction: Contract documentation, change orders, lien waivers, safety training records, subcontractor agreements, insurance certificates.
- Trades & Repair Services: Work orders, warranty documentation, licensing/certification records, safety training, vehicle maintenance logs.
- Personal Services: Client consent forms, health/allergy information if applicable, appointment records, payment documentation.
- Funeral Services: Vital records handling, FTC Funeral Rule compliance documentation, pre-need contracts, trust fund records, cremation authorizations.
- Manufacturing / Warehouse: Quality control records, OSHA documentation, inventory tracking, supplier certifications, product recall documentation.
- Restaurant / Hospitality: Health department inspection records, food safety training, alcohol licensing, tip reporting, employee scheduling records.
- Real Estate: Transaction files, agency disclosures, earnest money documentation, commission agreements, trust account records.

IMPORTANT GUIDANCE DISCLAIMER:
This assessment provides educational guidance on document management best practices. It is NOT a professional audit, compliance certification, or legal advice. Recommendations are based on general best practices and the user's self-reported answers. Always consult qualified professionals for specific compliance requirements.

Return this JSON structure with CONCISE, ACTIONABLE content:
{
  "score": [calculated number 0-500],
  "riskLevel": "[HIGH RISK/ELEVATED RISK/MODERATE/STRONG]",
  "criticalCount": [number of critical issues],
  "attentionCount": [number of attention items],
  "goodCount": [number of good practices],
  "executiveSummary": "[2-3 sentences max: Key strengths, main vulnerabilities, bottom line. Be direct.]",
  "priorities": [
    {"title": "[Specific action]", "reason": "[One sentence why]"},
    {"title": "[Action]", "reason": "[Why]"},
    {"title": "[Action]", "reason": "[Why]"}
  ],
  "criticalIssues": [
    {
      "topic": "[Specific topic from their weak answer]",
      "answer": "[Quote their actual answer]",
      "risk": "[1 sentence: specific consequence for their industry]",
      "fix": "[1-2 sentences: exactly what to do]",
      "effort": "[Time estimate]"
    }
  ],
  "attentionIssues": [
    {
      "topic": "[Topic]",
      "answer": "[Their answer]",
      "fix": "[1 sentence: specific improvement]",
      "effort": "[Time estimate]"
    }
  ],
  "goodPractices": [
    "[Short phrase describing what they're doing well]"
  ],
  "actionPlan": {
    "week1": ["[Quick win]", "[Quick win]"],
    "week2to4": ["[Larger task]", "[Task]"],
    "ongoing": ["[Maintenance task]"]
  }
}

REQUIREMENTS:
- Return ONLY valid JSON, no markdown or explanation
- BE CONCISE. Risk = 1 sentence. Fix = 1-2 sentences max. No fluff.
- Be specific to ${businessInfo.industry} industry
- Reference their actual answers
- Critical = serious gaps, Attention = improvements needed, Good = strong answers
- Actionable with specific steps`;


    try {
      const response = await fetch('/api/generate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate report');
      }
      
      let reportData;
      try {
        const reportText = data.report || '';
        const cleanJson = reportText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        reportData = JSON.parse(cleanJson);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.log('Raw response:', data.report);
        const fallbackReport = generateClientSideReport(answers, businessInfo);
        reportData = fallbackReport;
      }
      
      setPendingReport(reportData);
      setReportReady(true);
      setReportGenerating(false);
      
      if (businessInfo.email) {
        sendReportEmail(businessInfo.email, reportData);
      }
      
      if (loading) {
        setReport(reportData);
        setCurrentStep('report');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      const fallbackReport = generateClientSideReport(answers, businessInfo);
      setPendingReport(fallbackReport);
      setReportReady(true);
      setReportGenerating(false);
      
      if (loading) {
        setReport(fallbackReport);
        setCurrentStep('report');
        setLoading(false);
      }
    }
  };

  // Client-side scoring fallback function
  const generateClientSideReport = (answers, businessInfo) => {
    const scoringRules = {
      'retention_policy': { best: ['Yes, formal written policy'], partial: ['Informal guidelines exist'] },
      'retention_schedule': { best: ['Yes, detailed schedule by document type'], partial: ['General timeframes only'] },
      'destruction_process': { best: ['Yes, scheduled secure destruction'], partial: ['Ad-hoc shredding/deletion'] },
      'legal_hold': { best: ['Yes, formal legal hold process'], partial: ['Would figure it out if needed'] },
      'electronic_records': { best: ['Yes, all digital records covered'], partial: ['Some digital records included'] },
      'filing_system': { best: ['Yes, standardized system enforced'], partial: ['Guidelines exist but inconsistent'] },
      'retrieval_time': { best: ['Within hours'], partial: ['Within 1-2 days'] },
      'personnel_files': { best: ['Yes, properly separated'], partial: ['Partially separated'] },
      'i9_storage': { best: ['Yes, separate and organized'], partial: ['Mixed with other HR files'] },
      'backup_documentation': { best: ['Yes, offsite/cloud backup'], partial: ['Some documents backed up'] },
      'timekeeping': { best: ['Yes, automated time tracking'], partial: ['Manual but consistent'] },
      'pay_records': { best: ['Yes, 3+ years retained'], partial: ['Keep 1-2 years'] },
      'contract_management': { best: ['Yes, centralized contract system'], partial: ['Most contracts filed'] },
      'version_control': { best: ['Yes, dated versions archived'], partial: ['Keep current version only'] },
      'audit_trail': { best: ['Yes, system logs all changes'], partial: ['Partial tracking'] },
      'labor_posters': { best: ['Yes, current and displayed'], partial: ['Have posters but may be outdated'] },
      'training_records': { best: ['Yes, tracked with completion dates'], partial: ['Some training documented'] },
      'policy_acknowledgments': { best: ['Yes, for handbook and all key policies'], partial: ['Handbook only'] },
      'osha_logs': { best: ['Yes, current OSHA 300 logs'], partial: ['Maintain but may be incomplete'] },
      'license_tracking': { best: ['Yes, with renewal reminders'], partial: ['Track some but not all'] },
      'file_access': { best: ['Yes, role-based access controls'], partial: ['Somewhat limited'] },
      'physical_security': { best: ['Yes, locked and restricted', 'Not applicable (all digital)'], partial: ['Partially secured'] },
      'digital_security': { best: ['Yes, comprehensive digital security'], partial: ['Basic password protection'] },
      'offboarding_access': { best: ['Yes, same-day removal process'], partial: ['Within a few days'] },
      'confidentiality_agreements': { best: ['Yes, required for relevant roles'], partial: ['Sometimes'] }
    };

    const topicLabels = {
      'retention_policy': 'Document Retention Policy',
      'retention_schedule': 'Retention Schedule',
      'destruction_process': 'Document Destruction Process',
      'legal_hold': 'Legal Hold Process',
      'electronic_records': 'Electronic Records Management',
      'filing_system': 'Filing System',
      'retrieval_time': 'Document Retrieval Time',
      'personnel_files': 'Personnel File Organization',
      'i9_storage': 'I-9 Form Storage',
      'backup_documentation': 'Document Backup',
      'timekeeping': 'Timekeeping Records',
      'pay_records': 'Payroll Record Retention',
      'contract_management': 'Contract Management',
      'version_control': 'Policy Version Control',
      'audit_trail': 'Audit Trail',
      'labor_posters': 'Labor Law Posters',
      'training_records': 'Training Documentation',
      'policy_acknowledgments': 'Policy Acknowledgments',
      'osha_logs': 'OSHA Injury Logs',
      'license_tracking': 'License Tracking',
      'file_access': 'File Access Controls',
      'physical_security': 'Physical File Security',
      'digital_security': 'Digital File Security',
      'offboarding_access': 'Offboarding Access Removal',
      'confidentiality_agreements': 'Confidentiality Agreements'
    };

    const fixSuggestions = {
      'retention_policy': 'Create a formal document retention policy. Templates are available from SHRM (Society for Human Resource Management) and industry associations.',
      'retention_schedule': 'Build a retention schedule listing document types and retention periods. Consult your state requirements and industry regulations.',
      'destruction_process': 'Implement scheduled document destruction. Use cross-cut shredders or certified destruction services. Document what was destroyed and when.',
      'legal_hold': 'Create a legal hold policy and process. Train managers to recognize when litigation is anticipated and how to preserve documents.',
      'electronic_records': 'Extend your retention policy to cover emails, cloud files, and digital records. Use archiving solutions for email retention.',
      'filing_system': 'Standardize folder structures and naming conventions. Document the system and train all staff.',
      'retrieval_time': 'Improve organization so any document can be found within 24-48 hours. Consider document management software.',
      'personnel_files': 'Separate employee files into: (1) general personnel, (2) payroll/tax, (3) medical/FMLA, (4) I-9 forms.',
      'i9_storage': 'Store I-9 forms separately from other HR files. These must be produced within 3 days of government request.',
      'backup_documentation': 'Implement offsite or cloud backup for critical documents. Test restoration regularly.',
      'timekeeping': 'Implement automated time tracking. This provides accurate records for wage and hour compliance.',
      'pay_records': 'Retain payroll records for at least 3 years (federal) or longer per your state requirements.',
      'contract_management': 'Centralize contract storage. Use contract management software or a dedicated filing system.',
      'version_control': 'Archive dated versions of all policies and handbooks. Document when each version was in effect.',
      'audit_trail': 'Use accounting software with audit trail features. Enable logging for financial system changes.',
      'labor_posters': 'Update labor law posters annually. Many states require new posters each year. Use a poster compliance service.',
      'training_records': 'Track all required training with completion dates and signed acknowledgments.',
      'policy_acknowledgments': 'Require signed acknowledgments for employee handbook and all key policies. Keep indefinitely.',
      'osha_logs': 'Maintain OSHA Form 300/300A logs if you have 10+ employees. Keep for 5 years.',
      'license_tracking': 'Create a license/certification tracking system with renewal reminders 60-90 days in advance.',
      'file_access': 'Implement role-based access controls. Not everyone needs access to everything.',
      'physical_security': 'Store sensitive paper files in locked cabinets in restricted areas.',
      'digital_security': 'Implement password protection, encryption for sensitive data, and access logging.',
      'offboarding_access': 'Create an offboarding checklist that includes same-day access removal from all systems.',
      'confidentiality_agreements': 'Require NDAs (Non-Disclosure Agreements) for roles with access to sensitive information.'
    };

    let score = 0;
    const criticalIssues = [];
    const attentionIssues = [];
    const goodPractices = [];

    Object.entries(answers).forEach(([questionId, answer]) => {
      const rules = scoringRules[questionId];
      const topic = topicLabels[questionId] || questionId;
      const fix = fixSuggestions[questionId] || 'Review and improve this area.';
      
      if (!rules) {
        console.log('No scoring rule for:', questionId);
        return;
      }
      
      const isBest = rules.best.some(opt => answer === opt || answer.includes(opt));
      const isPartial = rules.partial.some(opt => answer === opt || answer.includes(opt));
      
      if (isBest) {
        score += 20;
        goodPractices.push(topic);
      } else if (isPartial) {
        score += 12;
        attentionIssues.push({
          topic,
          answer,
          fix,
          effort: '1-2 hours'
        });
      } else {
        criticalIssues.push({
          topic,
          answer,
          risk: `Missing ${topic.toLowerCase()} can expose your organization to audit findings, legal liability, or operational problems.`,
          fix,
          effort: '2-4 hours'
        });
      }
    });

    const riskLevel = score < 200 ? 'HIGH RISK' : score < 300 ? 'ELEVATED RISK' : score < 400 ? 'MODERATE' : 'STRONG';

    return {
      score,
      riskLevel,
      criticalCount: criticalIssues.length,
      attentionCount: attentionIssues.length,
      goodCount: goodPractices.length,
      executiveSummary: `Your organization scored ${score}/500 on audit readiness. ${criticalIssues.length > 0 ? `There are ${criticalIssues.length} critical gaps that need immediate attention.` : 'No critical gaps identified.'} ${goodPractices.length > 0 ? `You're doing well in ${goodPractices.length} areas.` : ''}`,
      priorities: criticalIssues.slice(0, 3).map(issue => ({
        title: `Address ${issue.topic}`,
        reason: issue.risk
      })),
      criticalIssues,
      attentionIssues,
      goodPractices,
      actionPlan: {
        week1: criticalIssues.slice(0, 2).map(i => `Address ${i.topic}`),
        week2to4: attentionIssues.slice(0, 3).map(i => `Improve ${i.topic}`),
        ongoing: ['Maintain documentation practices', 'Review retention schedules quarterly']
      }
    };
  };

  // Start background report generation when entering last category
  useEffect(() => {
    if (currentCategory === categories.length - 1 && Object.keys(answers).length >= 20 && !reportGenerating && !reportReady) {
      generateReportAPI();
    }
  }, [currentCategory, answers]);

  // Transition to report when ready
  useEffect(() => {
    if (loading && reportReady && pendingReport) {
      setReport(pendingReport);
      setCurrentStep('report');
      setLoading(false);
      
      if (businessInfo.email) {
        sendReportEmail(businessInfo.email, pendingReport);
      }
    }
  }, [loading, reportReady, pendingReport]);

  const currentCategoryData = categories[currentCategory];
  const allQuestionsAnswered = currentCategoryData?.questions.every(q => answers[q.id]);
  const totalQuestions = categories.reduce((sum, cat) => sum + cat.questions.length, 0);
  const answeredQuestions = Object.keys(answers).length;

  const baseStyles = {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.darkBg} 0%, ${colors.black} 100%)`,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: colors.white,
    padding: 'clamp(1rem, 4vw, 2rem)',
    overflowX: 'hidden'
  };

  const globalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { overflow-x: hidden; }
    html { font-size: 16px; }
    body { -webkit-text-size-adjust: 100%; }
    input, select, button { font-family: inherit; font-size: 16px; }
    input, select { min-height: 48px; }
    input[type="email"], input[type="text"] { -webkit-appearance: none; appearance: none; }
    button { min-height: 48px; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    ::selection { background: ${colors.green}; color: white; }
    @media (max-width: 480px) {
      html { font-size: 18px; }
    }
  `;

  // Privacy Policy Page
  if (currentStep === 'privacy') {
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <button onClick={() => setCurrentStep('intro')} style={{ background: 'transparent', border: 'none', color: colors.green, cursor: 'pointer', marginBottom: '1.5rem', fontSize: '1rem' }}>← Back to Home</button>
          
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1.5rem' }}>Privacy Policy</h1>
          <p style={{ color: colors.gray, marginBottom: '1rem' }}>Last updated: {new Date().toLocaleDateString()}</p>
          
          <div style={{ color: colors.grayLight, lineHeight: 1.8, fontSize: '0.95rem' }}>
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Information We Collect</h2>
            <p>When you use AuditReadyIQ, we collect:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Business name and industry (to customize your report)</li>
              <li>Email address (to deliver your report)</li>
              <li>Your assessment answers (to generate your report)</li>
              <li>Payment information (processed securely by PayPal)</li>
            </ul>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>How We Use Your Information</h2>
            <p>We use your information solely to:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Generate your personalized audit readiness report</li>
              <li>Deliver your report via email</li>
              <li>Process your payment</li>
              <li>Improve our assessment tool</li>
            </ul>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Data Storage</h2>
            <p>Your report is stored locally in your browser for your convenience. We do not store your complete assessment answers on our servers after your report is generated.</p>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Third-Party Services</h2>
            <p>We use:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>PayPal for payment processing</li>
              <li>EmailJS for report delivery</li>
              <li>Anthropic's Claude for report generation</li>
            </ul>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Contact</h2>
            <p>Questions? Contact us at <a href="mailto:info@techshieldkc.com" style={{ color: colors.green }}>info@techshieldkc.com</a></p>
          </div>
          
          <p style={{ color: colors.grayDark, fontSize: '0.8rem', marginTop: '3rem' }}>© {new Date().getFullYear()} AuditReadyIQ™ by TechShield KC LLC</p>
        </div>
      </div>
    );
  }

  // Terms of Service Page
  if (currentStep === 'terms') {
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <button onClick={() => setCurrentStep('intro')} style={{ background: 'transparent', border: 'none', color: colors.green, cursor: 'pointer', marginBottom: '1.5rem', fontSize: '1rem' }}>← Back to Home</button>
          
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '1.5rem' }}>Terms of Service</h1>
          <p style={{ color: colors.gray, marginBottom: '1rem' }}>Last updated: {new Date().toLocaleDateString()}</p>
          
          <div style={{ color: colors.grayLight, lineHeight: 1.8, fontSize: '0.95rem' }}>
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Educational Guidance Only</h2>
            <p style={{ fontWeight: 600, color: colors.green }}>IMPORTANT: AuditReadyIQ provides educational guidance only. It is NOT:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>A professional audit</li>
              <li>A compliance certification</li>
              <li>Legal advice</li>
              <li>A guarantee of audit readiness</li>
              <li>A substitute for professional consultation</li>
            </ul>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Self-Reported Information</h2>
            <p>Your report is based entirely on the information you provide. We cannot verify the accuracy of your answers. The quality of your report depends on the accuracy of your inputs.</p>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>No Guarantees</h2>
            <p>We do not guarantee that following our recommendations will:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Prevent audit findings</li>
              <li>Ensure regulatory compliance</li>
              <li>Protect you from legal liability</li>
              <li>Meet any specific standard or requirement</li>
            </ul>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Professional Advice</h2>
            <p>For specific compliance requirements, legal obligations, or audit preparation, you should consult with qualified professionals including:</p>
            <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
              <li>Attorneys familiar with your industry</li>
              <li>Certified Public Accountants (CPAs)</li>
              <li>HR consultants or SHRM-certified professionals</li>
              <li>Industry-specific compliance experts</li>
            </ul>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Limitation of Liability</h2>
            <p>TechShield KC LLC and AuditReadyIQ shall not be liable for any damages arising from the use of this tool or reliance on its recommendations.</p>
            
            <h2 style={{ color: colors.white, fontSize: '1.25rem', marginTop: '2rem', marginBottom: '1rem' }}>Refund Policy</h2>
            <p>Due to the immediate delivery of digital reports, all sales are final. If you experience technical issues, contact us at <a href="mailto:info@techshieldkc.com" style={{ color: colors.green }}>info@techshieldkc.com</a>.</p>
          </div>
          
          <p style={{ color: colors.grayDark, fontSize: '0.8rem', marginTop: '3rem' }}>© {new Date().getFullYear()} AuditReadyIQ™ by TechShield KC LLC</p>
        </div>
      </div>
    );
  }

  // Intro Screen - Landing Page
  if (currentStep === 'intro') {
    const audiences = {
      business: { label: 'Business', stat: '64% of small businesses cannot locate critical documents when needed', subtext: 'costing an average of $35,000 in audit failures' },
      healthcare: { label: 'Healthcare', stat: '89% of healthcare organizations experienced a compliance issue', subtext: 'related to documentation in the past two years' },
      nonprofit: { label: 'Nonprofits', stat: '68% of nonprofits cannot produce required documents within 72 hours', subtext: 'risking IRS audit failures' },
      financial: { label: 'Financial', stat: 'Financial firms must retain records for 6+ years', subtext: 'yet 45% have incomplete documentation' }
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0a0a0a', color: '#e5e5e5', fontFamily: "'Inter', system-ui, sans-serif", lineHeight: 1.6 }}>
        <style>{globalStyles}</style>
        
        {/* Gradient */}
        <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(16,185,129,0.08) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          
          {/* Saved Report Banner */}
          {hasStoredReport && report && (
            <div style={{ 
              background: 'linear-gradient(135deg, #1a472a 0%, #0f2818 100%)', 
              borderBottom: '2px solid #22c55e',
              padding: '16px 24px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#fff', fontSize: '1rem', marginBottom: '12px' }}>
                📄 You have a saved report for <strong>{businessInfo.name}</strong>
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setCurrentStep('report')}
                  style={{
                    background: '#22c55e',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    color: '#000',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  View My Report
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem('auditreadyiq_report');
                    localStorage.removeItem('auditreadyiq_business');
                    setHasStoredReport(false);
                    setReport(null);
                    setBusinessInfo({ name: '', industry: '', size: '', email: '' });
                    setPaymentComplete(false);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid #666',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    color: '#999',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem'
                  }}
                >
                  Start Fresh
                </button>
              </div>
            </div>
          )}

          {/* Hero */}
          <section style={{ padding: 'clamp(30px, 8vw, 60px) 24px 48px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            
            <h1 style={{ fontSize: 'clamp(3.2rem, 15vw, 3.5rem)', fontWeight: 700, marginBottom: '16px', letterSpacing: '-0.02em' }}>
              <span style={{ color: '#fff' }}>AuditReady</span>
              <span style={{ color: colors.green }}>IQ</span>
              <span style={{ color: colors.green, fontSize: '0.5em', verticalAlign: 'super' }}>™</span>
            </h1>

            <p style={{ fontSize: '1.25rem', color: '#999', marginBottom: '8px' }}>
              Before the auditor shows up,
            </p>
            <p style={{ fontSize: '1.25rem', color: '#fff', marginBottom: '28px' }}>
              spend <span style={{ color: colors.green, fontWeight: 600 }}>under 10 minutes</span> finding out if you're ready.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px', marginBottom: '8px', fontSize: '1.05rem', color: '#666' }}>
              <span>✓ ~10 min</span>
              <span>✓ 25 questions</span>
              <span>✓ DOL & IRS</span>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#444', marginBottom: '20px' }}>
              (Department of Labor & Internal Revenue Service documentation standards)
            </p>

            {/* Plain English callout */}
            <div style={{ 
              backgroundColor: 'rgba(255,255,255,0.03)', 
              border: '1px solid #333', 
              borderRadius: '10px', 
              padding: '16px 20px', 
              marginBottom: '28px',
              maxWidth: '480px',
              margin: '0 auto 28px'
            }}>
              <p style={{ fontSize: '1rem', color: '#ccc', margin: 0 }}>
                <strong style={{ color: '#fff' }}>No accounting degree needed.</strong> Most audit prep guides are 50+ pages of compliance jargon. Ours? <span style={{ color: colors.green, fontWeight: 600 }}>Plain-English guidance</span> so you know what matters <em>before</em> the auditor arrives.
              </p>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
              {Object.entries(audiences).map(([key, val]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    padding: '10px 16px',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    backgroundColor: activeTab === key ? colors.green : 'transparent',
                    color: activeTab === key ? '#000' : '#666',
                    border: activeTab === key ? 'none' : '1px solid #333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontFamily: 'inherit'
                  }}
                >
                  {val.label}
                </button>
              ))}
            </div>

            {/* Stat box */}
            <div style={{ backgroundColor: 'rgba(16,185,129,0.08)', borderLeft: `4px solid ${colors.green}`, borderRadius: '8px', padding: '20px 24px', textAlign: 'center', marginBottom: '32px' }}>
              <p style={{ fontSize: '1.15rem', margin: 0, lineHeight: 1.6 }}>
                <span style={{ color: colors.green, fontWeight: 600 }}>{audiences[activeTab].stat}</span>
                <span style={{ color: '#888' }}>, {audiences[activeTab].subtext}.</span>
              </p>
            </div>

            <button onClick={() => setCurrentStep('business')} style={{ display: 'inline-block', backgroundColor: colors.green, color: '#000', padding: '18px 40px', fontSize: '1.15rem', fontWeight: 600, borderRadius: '8px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(16,185,129,0.3)', fontFamily: 'inherit' }}>
              Start Free Assessment →
            </button>
            <p style={{ fontSize: '1rem', color: '#555', marginTop: '16px' }}>
              Free score preview • Full report: <span style={{ color: colors.green }}>$29.99</span>
            </p>
          </section>

          {/* Problem */}
          <section style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
              The problem with audit prep
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
              {[
                '💰 CPA firms charge $3,000+ for readiness assessments',
                '⏰ You only find gaps when the auditor finds them',
                '📁 "I know it\'s here somewhere" isn\'t a filing system'
              ].map((item, i) => (
                <div key={i} style={{ backgroundColor: '#141414', borderRadius: '8px', padding: '16px 20px', fontSize: '1.1rem', color: '#999', border: '1px solid #1a1a1a', textAlign: 'center' }}>
                  {item}
                </div>
              ))}
            </div>

            <p style={{ fontSize: '1.2rem', color: '#fff' }}>
              What if you knew <span style={{ color: colors.green }}>your gaps</span> before they did?
            </p>
          </section>

          {/* Credibility */}
          <section style={{ padding: '48px 24px', backgroundColor: '#0f0f0f' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
                Built on real requirements
              </h2>
              
              <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525', marginBottom: '16px' }}>
                <p style={{ fontSize: '1.05rem', color: '#ccc', lineHeight: 1.7, margin: 0 }}>
                  Based on requirements from the <span style={{ color: colors.green, fontWeight: 600 }}>DOL (Department of Labor)</span>, <span style={{ color: colors.green, fontWeight: 600 }}>IRS</span>, <span style={{ color: colors.green, fontWeight: 600 }}>OSHA</span>, and industry-specific regulations. The same standards auditors use.
                </p>
              </div>
              
              <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525' }}>
                <p style={{ fontSize: '1.05rem', color: '#ccc', lineHeight: 1.7, margin: 0 }}>
                  Created by someone who's actually sat across from auditors. <span style={{ color: '#fff', fontWeight: 500 }}>20 years running IT operations</span> from startups to 20k+ employee enterprises.
                </p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section style={{ padding: '48px 24px' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '28px' }}>
                Your report includes
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {[
                  { icon: '📁', title: 'Find Gaps', items: 'Retention, organization, access' },
                  { icon: '📊', title: 'Understand Risk', items: 'Industry-specific concerns' },
                  { icon: '✅', title: 'Action Plan', items: '30-day roadmap, time estimates' },
                  { icon: '🔗', title: 'Resources', items: 'Government guides, templates' }
                ].map((card, i) => (
                  <div key={i} style={{ backgroundColor: '#1a1a1a', borderRadius: '10px', padding: '20px', border: '1px solid #252525', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.75rem', marginBottom: '10px' }}>{card.icon}</div>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>{card.title}</h3>
                    <p style={{ fontSize: '0.95rem', color: '#666', margin: 0, lineHeight: 1.5 }}>{card.items}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Sample Report */}
          <section style={{ padding: '48px 24px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '24px' }}>
              Sample report preview
            </h2>

            <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525' }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: colors.green }}>
                  296<span style={{ fontSize: '1rem', color: '#666' }}>/500</span>
                </div>
                <span style={{ backgroundColor: 'rgba(16,185,129,0.2)', color: colors.green, padding: '4px 12px', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 600 }}>ELEVATED RISK</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', marginBottom: '20px', fontSize: '1rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#e74c3c' }}>5</div>
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>Critical</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f39c12' }}>8</div>
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>Attention</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#27ae60' }}>12</div>
                  <div style={{ color: '#666', fontSize: '0.85rem' }}>Good</div>
                </div>
              </div>

              <div style={{ backgroundColor: '#1a1a1a', borderRadius: '8px', padding: '16px', marginBottom: '16px', textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#e74c3c', fontWeight: 600, fontSize: '1rem' }}>🔴 No Retention Policy</span>
                  <span style={{ backgroundColor: '#e74c3c', color: '#fff', padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600 }}>CRITICAL</span>
                </div>
                <p style={{ color: '#888', fontSize: '0.95rem', margin: 0 }}>
                  Fix: Create written retention policy. <span style={{ color: colors.green }}>⏱️ 2-4 hours</span>
                </p>
              </div>

              <div style={{ backgroundColor: '#1a1a1a', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <p style={{ color: '#fff', fontWeight: 600, fontSize: '1rem', margin: '0 0 12px' }}>🎯 30-Day Action Plan</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['Retention policy', 'File organization', 'I-9 audit'].map((item, i) => (
                    <span key={i} style={{ backgroundColor: '#252525', color: '#888', padding: '6px 12px', borderRadius: '6px', fontSize: '0.9rem' }}>{item}</span>
                  ))}
                  <span style={{ color: colors.green, fontSize: '0.9rem', padding: '6px' }}>+10 more</span>
                </div>
              </div>
            </div>
          </section>

          {/* Price Comparison */}
          <section style={{ padding: '48px 24px', backgroundColor: '#0f0f0f' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '28px' }}>
                Compare the cost
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div style={{ backgroundColor: '#141414', borderRadius: '12px', padding: '24px', border: '1px solid #252525' }}>
                  <p style={{ color: '#666', marginBottom: '8px', fontSize: '0.9rem' }}>CPA Readiness Review</p>
                  <p style={{ fontSize: '2rem', fontWeight: 700, color: '#888' }}>$3,000+</p>
                  <p style={{ color: '#555', fontSize: '0.85rem' }}>2-4 weeks</p>
                </div>
                <div style={{ backgroundColor: 'rgba(16,185,129,0.1)', borderRadius: '12px', padding: '24px', border: `2px solid ${colors.green}` }}>
                  <p style={{ color: colors.green, marginBottom: '8px', fontSize: '0.9rem' }}>AuditReadyIQ</p>
                  <p style={{ fontSize: '2rem', fontWeight: 700, color: colors.green }}>$29.99</p>
                  <p style={{ color: '#888', fontSize: '0.85rem' }}>~10 minutes</p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section style={{ padding: '48px 24px' }}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#fff', marginBottom: '28px', textAlign: 'center' }}>
                Common questions
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { q: 'Is this a real audit?', a: 'No. AuditReadyIQ provides educational guidance based on your self-reported answers. It is not an audit, compliance certification, or legal advice. Think of it as a readiness check-up, not a diagnosis.' },
                  { q: 'Will this guarantee I pass an audit?', a: 'No tool can guarantee audit outcomes. Our guidance helps you identify and address common documentation gaps, but actual audit results depend on many factors including your implementation and the specific auditor.' },
                  { q: 'What standards does this cover?', a: 'We cover general documentation best practices based on DOL (Department of Labor), IRS, OSHA (Occupational Safety and Health Administration), and common industry requirements. For specific regulatory compliance (HIPAA, SOC 2, etc.), consult specialized professionals.' },
                  { q: 'Why should I trust this over a free checklist?', a: 'Free checklists tell you what to have. We analyze your specific situation, prioritize your gaps by risk, and give you a concrete action plan with time estimates. Plus, the AI customizes recommendations to your industry.' },
                  { q: 'What if I need real audit help after?', a: 'Your report identifies gaps and provides actionable steps. If you need hands-on help, TechShield KC offers documentation consulting services, or we can recommend specialists in your area.' }
                ].map((faq, i) => (
                  <div key={i} style={{ backgroundColor: '#141414', borderRadius: '10px', padding: '20px', border: '1px solid #252525', textAlign: 'center' }}>
                    <p style={{ fontWeight: 600, color: '#fff', marginBottom: '8px', fontSize: '1.05rem' }}>{faq.q}</p>
                    <p style={{ color: '#888', margin: 0, lineHeight: 1.6, fontSize: '0.95rem' }}>{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA */}
          <section style={{ padding: '60px 24px', textAlign: 'center', backgroundColor: '#0a0a0a' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 600, color: '#fff', marginBottom: '16px' }}>
              Ready to find out where you stand?
            </h2>
            <p style={{ color: '#888', marginBottom: '28px', fontSize: '1.1rem' }}>
              ~10 minutes. 25 questions. Complete guidance.
            </p>
            <button onClick={() => setCurrentStep('business')} style={{ backgroundColor: colors.green, color: '#000', padding: '18px 48px', fontSize: '1.2rem', fontWeight: 600, borderRadius: '8px', border: 'none', cursor: 'pointer', boxShadow: '0 4px 20px rgba(16,185,129,0.3)', fontFamily: 'inherit' }}>
              Start Free Assessment →
            </button>
            <p style={{ fontSize: '0.9rem', color: '#555', marginTop: '16px' }}>
              Free score preview • Full report: $29.99
            </p>
          </section>

          {/* Footer */}
          <footer style={{ padding: '32px 24px', borderTop: '1px solid #1a1a1a', textAlign: 'center' }}>
            <p style={{ color: '#555', fontSize: '0.85rem', marginBottom: '12px' }}>
              © {new Date().getFullYear()} AuditReadyIQ™ by TechShield KC LLC
            </p>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
              <button onClick={() => setCurrentStep('privacy')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '0.85rem' }}>Privacy Policy</button>
              <button onClick={() => setCurrentStep('terms')} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '0.85rem' }}>Terms of Service</button>
              <a href="mailto:info@techshieldkc.com" style={{ color: '#666', textDecoration: 'none', fontSize: '0.85rem' }}>Contact</a>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  // Business Info Step
  if (currentStep === 'business') {
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>
              Personalize your report
            </h1>
            <p style={{ color: colors.gray }}>We'll customize your report with <strong style={{ color: colors.white }}>industry specific requirements</strong> and benchmarks.</p>
          </div>

          {/* Score explanation box */}
          <div style={{
            background: colors.darkCard,
            border: `1px solid ${colors.grayDark}33`,
            borderRadius: '10px',
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ fontSize: '0.9rem', color: colors.grayLight, margin: 0, lineHeight: 1.6 }}>
              <span style={{ color: colors.green, fontWeight: 600 }}>AuditReadyIQ™ Score:</span> 25 questions × 20 points each = <strong style={{ color: colors.white }}>500 max</strong>. Higher is better. Your score reflects documentation readiness based on DOL (Department of Labor), IRS, and OSHA standards.
            </p>
          </div>

          <div style={{ background: colors.darkCard, borderRadius: '12px', padding: '1.5rem', border: `1px solid ${colors.grayDark}22` }}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem' }}>
                Organization Name <span style={{ color: colors.green }}>*</span>
              </label>
              <input
                type="text"
                value={businessInfo.name}
                onChange={e => setBusinessInfo(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Your Organization Name"
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: colors.darkBg,
                  border: `1px solid ${colors.grayDark}44`,
                  borderRadius: '8px',
                  color: colors.white,
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem' }}>
                Industry <span style={{ color: colors.green }}>*</span>
              </label>
              <p style={{ fontSize: '0.8rem', color: colors.gray, marginBottom: '0.5rem' }}>This customizes your report with relevant compliance requirements</p>
              <select
                value={businessInfo.industry}
                onChange={e => setBusinessInfo(prev => ({ ...prev, industry: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: colors.darkBg,
                  border: `1px solid ${colors.grayDark}44`,
                  borderRadius: '8px',
                  color: businessInfo.industry ? colors.white : colors.gray,
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select your industry...</option>
                {industries.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem' }}>
                Organization Size <span style={{ color: colors.green }}>*</span>
              </label>
              <select
                value={businessInfo.size}
                onChange={e => setBusinessInfo(prev => ({ ...prev, size: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: colors.darkBg,
                  border: `1px solid ${colors.grayDark}44`,
                  borderRadius: '8px',
                  color: businessInfo.size ? colors.white : colors.gray,
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  cursor: 'pointer'
                }}
              >
                <option value="">Select size...</option>
                <option value="1-5">1-5 employees</option>
                <option value="6-20">6-20 employees</option>
                <option value="21-50">21-50 employees</option>
                <option value="51-100">51-100 employees</option>
                <option value="100+">100+ employees</option>
              </select>
            </div>

            <button
              onClick={() => setCurrentStep('assessment')}
              disabled={!businessInfo.name || !businessInfo.industry || !businessInfo.size}
              style={{
                width: '100%',
                padding: '1rem',
                background: businessInfo.name && businessInfo.industry && businessInfo.size ? colors.green : colors.darkBg,
                border: 'none',
                borderRadius: '8px',
                color: businessInfo.name && businessInfo.industry && businessInfo.size ? colors.white : colors.grayDark,
                fontSize: '1rem',
                fontWeight: 600,
                cursor: businessInfo.name && businessInfo.industry && businessInfo.size ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit'
              }}
            >
              Start Assessment →
            </button>
          </div>

          <button
            onClick={() => setCurrentStep('intro')}
            style={{
              display: 'block',
              margin: '1.5rem auto 0',
              background: 'transparent',
              border: 'none',
              color: colors.gray,
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Assessment Step
  if (currentStep === 'assessment') {
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>
              {currentCategoryData.icon} {currentCategoryData.title}
            </h1>
            <p style={{ color: colors.gray, fontSize: '0.9rem' }}>
              Category {currentCategory + 1} of {categories.length}
            </p>
          </div>

          {/* Progress */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: colors.gray }}>Progress</span>
              <span style={{ fontSize: '0.8rem', color: colors.green }}>{answeredQuestions}/{totalQuestions}</span>
            </div>
            <div style={{ height: '6px', background: colors.darkCard, borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(answeredQuestions / totalQuestions) * 100}%`, background: colors.green, borderRadius: '3px', transition: 'width 0.3s ease' }} />
            </div>
          </div>

          {/* Questions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {currentCategoryData.questions.map((question, qIndex) => (
              <div
                key={question.id}
                style={{
                  background: colors.darkCard,
                  border: `1px solid ${colors.grayDark}22`,
                  borderRadius: '12px',
                  padding: '1.25rem'
                }}
              >
                <p style={{ 
                  marginBottom: '0.5rem', 
                  fontWeight: 500,
                  lineHeight: 1.5,
                  fontSize: '0.95rem'
                }}>
                  {qIndex + 1}. {question.text}
                </p>
                
                {question.helper && (
                  <p style={{
                    fontSize: '0.8rem',
                    color: colors.gray,
                    marginBottom: '1rem',
                    lineHeight: 1.5,
                    paddingLeft: '0.75rem',
                    borderLeft: `2px solid ${colors.grayDark}44`
                  }}>
                    💡 {question.helper}
                  </p>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {question.options.map(option => (
                    <button
                      key={option}
                      onClick={() => handleAnswer(question.id, option)}
                      style={{
                        background: answers[question.id] === option ? colors.greenLight : colors.darkBg,
                        border: `1px solid ${answers[question.id] === option ? colors.greenBorder : colors.grayDark}33`,
                        borderRadius: '8px',
                        padding: '0.7rem 1rem',
                        color: answers[question.id] === option ? colors.green : colors.grayLight,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: '1.5rem',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                if (currentCategory > 0) {
                  setCurrentCategory(currentCategory - 1);
                } else {
                  setCurrentStep('business');
                }
              }}
              style={{
                background: colors.darkCard,
                border: `1px solid ${colors.grayDark}33`,
                borderRadius: '8px',
                padding: '0.875rem 1.25rem',
                minWidth: '100px',
                flex: '1 1 auto',
                color: colors.gray,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '0.95rem'
              }}
            >
              ← Back
            </button>
            
            {currentCategory < categories.length - 1 ? (
              <button
                onClick={() => setCurrentCategory(currentCategory + 1)}
                disabled={!allQuestionsAnswered}
                style={{
                  background: allQuestionsAnswered ? colors.green : colors.darkCard,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.875rem 1.25rem',
                  color: allQuestionsAnswered ? colors.white : colors.grayDark,
                  cursor: allQuestionsAnswered ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}
              >
                Next →
              </button>
            ) : (
              <button
                onClick={() => setCurrentStep('preview')}
                disabled={answeredQuestions < totalQuestions}
                style={{
                  background: answeredQuestions >= totalQuestions ? '#22c55e' : colors.darkCard,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.875rem 1.25rem',
                  color: answeredQuestions >= totalQuestions ? colors.white : colors.grayDark,
                  cursor: answeredQuestions >= totalQuestions ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}
              >
                See My Results →
              </button>
            )}
          </div>
        </div>

        {/* Paywall Modal */}
        {showPaywall && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            zIndex: 1000,
            overflowY: 'auto'
          }}>
            <div style={{
              background: colors.black,
              border: `1px solid ${colors.grayDark}44`,
              borderRadius: '16px',
              padding: '1.5rem',
              maxWidth: '420px',
              width: '100%',
              position: 'relative',
              margin: '1rem 0'
            }}>
              <button
                onClick={() => setShowPaywall(false)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'transparent',
                  border: 'none',
                  color: colors.gray,
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  lineHeight: 1
                }}
              >
                ×
              </button>

              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.75rem' }}>
                  Your Results Are Ready
                </h3>
                
                {(() => {
                  const { highRisk, mediumRisk, lowRisk, iqScore } = calculateRiskScore();
                  const riskLevel = iqScore < 200 ? 'High Risk' : iqScore < 300 ? 'Elevated Risk' : iqScore < 400 ? 'Moderate' : 'Strong';
                  const riskColor = iqScore < 200 ? '#ef4444' : iqScore < 300 ? '#eab308' : iqScore < 400 ? '#22c55e' : '#22c55e';
                  return (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      background: colors.darkBg,
                      border: `2px solid ${colors.green}`,
                      borderRadius: '8px',
                      padding: '0.5rem 1rem'
                    }}>
                      <span style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.green }}>{iqScore}</span>
                      <span style={{ color: colors.gray }}>/500</span>
                      <span style={{ color: riskColor, fontWeight: 600, fontSize: '0.8rem' }}>• {riskLevel}</span>
                      <span style={{ color: colors.gray, fontSize: '0.75rem' }}>({highRisk} critical)</span>
                    </div>
                  );
                })()}
              </div>
              
              <div style={{ 
                fontSize: '0.75rem', 
                color: colors.grayLight, 
                marginBottom: '1rem',
                lineHeight: 1.6
              }}>
                {[
                  'AuditReadyIQ™ score with risk level',
                  'All 25 areas analyzed individually',
                  'Your answer + why it matters + what to do',
                  'Industry-specific documentation guidance',
                  '30-day action roadmap'
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ color: colors.green }}>✓</span> {item}
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: colors.gray, marginBottom: '0.5rem', textAlign: 'center' }}>
                  Choose your plan:
                </div>
                
                <div 
                  onClick={() => setSelectedPlan('onetime')}
                  style={{
                    background: selectedPlan === 'onetime' ? colors.greenLight : colors.darkCard,
                    border: `2px solid ${selectedPlan === 'onetime' ? colors.green : colors.grayDark}44`,
                    borderRadius: '8px',
                    padding: '0.75rem',
                    marginBottom: '0.4rem',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: `2px solid ${selectedPlan === 'onetime' ? colors.green : colors.gray}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {selectedPlan === 'onetime' && (
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colors.green }} />
                        )}
                      </div>
                      <div style={{ fontWeight: 600, color: colors.white, fontSize: '0.9rem' }}>Full Report</div>
                    </div>
                    <div style={{ fontWeight: 700, color: colors.green, fontSize: '1rem' }}>
                      {appliedDiscount > 0 && appliedDiscount < 100 ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: colors.gray, marginRight: '8px' }}>$29.99</span>
                          ${discountedPrice.toFixed(2)}
                        </>
                      ) : '$29.99'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.35rem', 
                  fontSize: '0.8rem',
                  color: colors.grayLight
                }}>
                  Email address <span style={{ color: colors.green }}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={businessInfo.email || ''}
                  onChange={e => setBusinessInfo(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="you@yourbusiness.com"
                  style={{
                    width: '100%',
                    padding: '0.6rem 0.75rem',
                    background: colors.darkCard,
                    border: `1px solid ${colors.grayDark}44`,
                    borderRadius: '6px',
                    color: colors.white,
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={e => e.target.style.borderColor = colors.green}
                  onBlur={e => e.target.style.borderColor = `${colors.grayDark}44`}
                />
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!businessInfo.email || !businessInfo.email.includes('@')) {
                          setPromoError('Enter email first');
                          return;
                        }
                        if (!promoCode.trim()) return;
                        
                        try {
                          const response = await fetch('/api/validate-promo', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code: promoCode.trim().toUpperCase() })
                          });
                          const data = await response.json();
                          
                          if (data.valid) {
                            setAppliedDiscount(data.discount);
                            const newPrice = 29.99 * (1 - data.discount / 100);
                            setDiscountedPrice(newPrice);
                            setPromoError('');
                            
                            if (data.discount === 100) {
                              setPaymentComplete(true);
                              generateReport();
                            }
                          } else {
                            setPromoError(data.message || 'Invalid code');
                            setAppliedDiscount(0);
                            setDiscountedPrice(29.99);
                          }
                        } catch (error) {
                          setPromoError('Error validating code');
                        }
                      }
                    }}
                    placeholder="Promo code (optional)"
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.grayDark}`,
                      background: colors.darkCard,
                      color: colors.white,
                      fontFamily: 'inherit',
                      fontSize: '0.85rem'
                    }}
                  />
                  <button
                    data-apply-promo
                    onClick={async () => {
                      if (!businessInfo.email || !businessInfo.email.includes('@')) {
                        setPromoError('Enter email first');
                        return;
                      }
                      if (!promoCode.trim()) return;
                      
                      const code = promoCode.trim().toUpperCase();
                      
                      // Client-side fallback for known promo codes
                      const validCodes = {
                        'TECHSHIELD100': 100,
                        'FRIEND100': 100,
                        'LAUNCH50': 50,
                        'EARLYBIRD': 25
                      };
                      
                      if (validCodes[code]) {
                        const discount = validCodes[code];
                        setAppliedDiscount(discount);
                        const newPrice = 29.99 * (1 - discount / 100);
                        setDiscountedPrice(newPrice);
                        setPromoError('');
                        
                        if (discount === 100) {
                          setPaymentComplete(true);
                          setShowPaywall(false);
                          generateReport();
                        }
                        return;
                      }
                      
                      // Try API validation
                      try {
                        const response = await fetch('/api/validate-promo', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ code: code })
                        });
                        const data = await response.json();
                        
                        if (data.valid) {
                          setAppliedDiscount(data.discount);
                          const newPrice = 29.99 * (1 - data.discount / 100);
                          setDiscountedPrice(newPrice);
                          setPromoError('');
                          
                          if (data.discount === 100) {
                            setPaymentComplete(true);
                            setShowPaywall(false);
                            generateReport();
                          }
                        } else {
                          setPromoError(data.message || 'Invalid code');
                          setAppliedDiscount(0);
                          setDiscountedPrice(29.99);
                        }
                      } catch (error) {
                        // If API fails, code wasn't in our local list either
                        setPromoError('Invalid code');
                        setAppliedDiscount(0);
                        setDiscountedPrice(29.99);
                      }
                    }}
                    style={{
                      padding: '0.6rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      background: colors.green,
                      color: colors.white,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '0.85rem'
                    }}
                  >
                    Apply
                  </button>
                </div>
                {promoError && (
                  <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.35rem' }}>{promoError}</p>
                )}
                {appliedDiscount > 0 && appliedDiscount < 100 && (
                  <p style={{ color: '#22c55e', fontSize: '0.75rem', marginTop: '0.35rem', fontWeight: 600 }}>
                    ✓ {appliedDiscount}% off! Pay ${discountedPrice.toFixed(2)}
                  </p>
                )}
                {appliedDiscount === 100 && (
                  <p style={{ color: '#22c55e', fontSize: '0.75rem', marginTop: '0.35rem', fontWeight: 600 }}>
                    ✓ 100% discount applied! Generating report...
                  </p>
                )}
              </div>

              {businessInfo.email && businessInfo.email.includes('@') && appliedDiscount < 100 && (
                <div style={{ marginBottom: '1rem' }}>
                  {paypalLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray }}>
                      <div style={{ marginBottom: '0.5rem' }}>⏳</div>
                      Loading payment options...
                    </div>
                  ) : (
                    <div ref={paypalRef} style={{ minHeight: '150px' }} />
                  )}
                </div>
              )}

              {(!businessInfo.email || !businessInfo.email.includes('@')) && (
                <p style={{ color: colors.gray, fontSize: '0.8rem', textAlign: 'center', marginTop: '0.5rem' }}>
                  Enter your email to see payment options
                </p>
              )}

              <p style={{ fontSize: '0.7rem', color: colors.grayDark, textAlign: 'center', marginTop: '1rem', lineHeight: 1.5 }}>
                This is educational guidance only, not an audit or legal advice.<br />
                <button onClick={() => setCurrentStep('terms')} style={{ background: 'none', border: 'none', color: colors.grayDark, textDecoration: 'underline', cursor: 'pointer', fontSize: '0.7rem' }}>Terms of Service</button>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Preview Step (Score Preview before Paywall)
  if (currentStep === 'preview') {
    const { highRisk, mediumRisk, lowRisk, iqScore } = calculateRiskScore();
    const riskLevel = iqScore < 200 ? 'HIGH RISK' : iqScore < 300 ? 'ELEVATED RISK' : iqScore < 400 ? 'MODERATE' : 'STRONG';

    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem 1rem' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: colors.white, marginBottom: '0.5rem' }}>
              Assessment Complete!
            </h1>
            <p style={{ color: colors.gray, fontSize: '0.9rem' }}>{businessInfo.name}</p>
          </div>

          {/* Locked Score Card */}
          <div style={{
            background: colors.darkCard,
            borderRadius: '16px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '1.5rem',
            border: `2px solid ${colors.green}`,
            position: 'relative'
          }}>
            <div style={{ fontSize: '4rem', fontWeight: 700, color: colors.green, filter: 'blur(8px)' }}>
              {iqScore}
            </div>
            <div style={{ color: colors.gray, fontSize: '1rem', marginBottom: '1rem' }}>out of 500</div>
            <div style={{
              display: 'inline-block',
              background: '#f59e0b',
              color: 'white',
              padding: '0.5rem 1.5rem',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: 600,
              filter: 'blur(4px)'
            }}>
              {riskLevel}
            </div>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(0,0,0,0.8)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: `1px solid ${colors.green}`
            }}>
              <span style={{ fontSize: '1.5rem' }}>🔒</span>
              <span style={{ color: colors.white, marginLeft: '0.5rem', fontWeight: 600 }}>Unlock Your Score</span>
            </div>
          </div>

          {/* Stats - REAL numbers, no blur */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              background: '#fee2e2',
              padding: '1rem 1.5rem',
              borderRadius: '10px',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#dc2626' }}>{highRisk}</div>
              <div style={{ fontSize: '0.75rem', color: '#991b1b' }}>Critical</div>
            </div>
            <div style={{
              background: '#fef3c7',
              padding: '1rem 1.5rem',
              borderRadius: '10px',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#f59e0b' }}>{mediumRisk}</div>
              <div style={{ fontSize: '0.75rem', color: '#92400e' }}>Attention</div>
            </div>
            <div style={{
              background: '#d1fae5',
              padding: '1rem 1.5rem',
              borderRadius: '10px',
              textAlign: 'center',
              minWidth: '100px'
            }}>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#10b981' }}>{lowRisk}</div>
              <div style={{ fontSize: '0.75rem', color: '#065f46' }}>Good</div>
            </div>
          </div>

          {/* Sample Report Preview - BLURRED */}
          <div style={{
            background: colors.darkCard,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ color: colors.white, fontSize: '1rem', marginBottom: '1rem' }}>
              📋 Sample from your report:
            </h3>
            <div style={{ position: 'relative' }}>
              <div style={{ filter: 'blur(3px)', pointerEvents: 'none' }}>
                <div style={{ background: colors.darkBg, padding: '0.75rem', borderRadius: '8px', marginBottom: '0.5rem', borderLeft: '3px solid #dc2626' }}>
                  <span style={{ color: '#dc2626', fontWeight: 600 }}>Critical:</span>
                  <span style={{ color: colors.gray }}> Your document retention policy needs immediate attention to meet audit requirements...</span>
                </div>
                <div style={{ background: colors.darkBg, padding: '0.75rem', borderRadius: '8px', marginBottom: '0.5rem', borderLeft: '3px solid #f59e0b' }}>
                  <span style={{ color: '#f59e0b', fontWeight: 600 }}>Attention:</span>
                  <span style={{ color: colors.gray }}> Employee file organization should be standardized with consistent naming conventions...</span>
                </div>
                <div style={{ background: colors.darkBg, padding: '0.75rem', borderRadius: '8px', borderLeft: '3px solid #10b981' }}>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>Strong:</span>
                  <span style={{ color: colors.gray }}> Tax records are properly maintained with appropriate backup procedures...</span>
                </div>
              </div>
            </div>
          </div>

          {/* What's in the Report */}
          <div style={{
            background: colors.greenLight,
            border: `1px solid ${colors.greenBorder}`,
            borderRadius: '12px',
            padding: '1.25rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <p style={{ fontWeight: 600, color: colors.white, marginBottom: '0.75rem', fontSize: '0.95rem' }}>
              Your personalized report includes:
            </p>
            <div style={{ fontSize: '0.9rem', color: colors.grayLight, lineHeight: 1.9 }}>
              <div><span style={{ color: colors.green }}>✓</span> Your exact AuditReadyIQ™ score</div>
              <div><span style={{ color: colors.green }}>✓</span> Detailed analysis of all 25 documentation areas</div>
              <div><span style={{ color: colors.green }}>✓</span> Specific risks for {businessInfo.industry || 'your'} organizations</div>
              <div><span style={{ color: colors.green }}>✓</span> Step-by-step fix instructions with time estimates</div>
              <div><span style={{ color: colors.green }}>✓</span> 30-day action plan prioritized by impact</div>
              <div><span style={{ color: colors.green }}>✓</span> PDF download for your records</div>
            </div>
          </div>

          {/* Unlock Button */}
          <button
            onClick={() => setShowPaywall(true)}
            style={{
              width: '100%',
              background: colors.green,
              border: 'none',
              borderRadius: '10px',
              padding: '1rem',
              color: colors.white,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '1.1rem',
              fontWeight: 600,
              boxShadow: `0 4px 20px ${colors.green}40`,
              marginBottom: '1rem'
            }}
          >
            🔓 Unlock Full Report $29.99
          </button>

          <p style={{ textAlign: 'center', color: colors.grayDark, fontSize: '0.75rem' }}>
            One-time purchase • Instant access • PDF download included
          </p>
          
          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: `1px solid ${colors.grayDark}33` }}>
            <p style={{ fontSize: '0.7rem', color: colors.grayDark }}>
              <button onClick={() => setCurrentStep('privacy')} style={{ background: 'none', border: 'none', color: colors.grayDark, cursor: 'pointer', marginRight: '1rem' }}>Privacy Policy</button>
              <button onClick={() => setCurrentStep('terms')} style={{ background: 'none', border: 'none', color: colors.grayDark, cursor: 'pointer' }}>Terms of Service</button>
            </p>
          </div>
        </div>

        {/* Paywall Modal */}
        {showPaywall && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '0.5rem',
            overflowY: 'auto'
          }}>
            <div style={{
              background: colors.black,
              borderRadius: '16px',
              padding: 'clamp(1rem, 4vw, 2rem)',
              maxWidth: '450px',
              width: '100%',
              maxHeight: '95vh',
              overflowY: 'auto',
              border: `1px solid ${colors.grayDark}33`,
              margin: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: colors.white }}>Unlock Your Report</h2>
                <button
                  onClick={() => setShowPaywall(false)}
                  style={{ background: 'none', border: 'none', color: colors.gray, cursor: 'pointer', fontSize: '1.5rem' }}
                >×</button>
              </div>

              {/* Email field */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.grayLight, fontSize: '0.85rem' }}>
                  Email (for your report)
                </label>
                <input
                  type="email"
                  value={businessInfo.email}
                  onChange={(e) => setBusinessInfo({...businessInfo, email: e.target.value})}
                  placeholder="you@company.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.grayDark}`,
                    background: colors.darkCard,
                    color: colors.white,
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Plan selection */}
              <div style={{ marginBottom: '1rem' }}>
                <div 
                  onClick={() => setSelectedPlan('onetime')}
                  style={{
                    background: selectedPlan === 'onetime' ? colors.greenLight : colors.darkCard,
                    border: `2px solid ${selectedPlan === 'onetime' ? colors.green : colors.grayDark}44`,
                    borderRadius: '8px',
                    padding: '0.75rem',
                    cursor: 'pointer',
                    marginBottom: '0.5rem'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: colors.white, fontWeight: 500 }}>One-Time Report</span>
                    <span style={{ color: colors.green, fontWeight: 600 }}>
                      {appliedDiscount > 0 ? `$${discountedPrice.toFixed(2)}` : '$29.99'}
                    </span>
                  </div>
                  {appliedDiscount > 0 && (
                    <div style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.25rem' }}>
                      <span style={{ textDecoration: 'line-through' }}>$29.99</span>
                      <span style={{ color: colors.green, marginLeft: '0.5rem' }}>{appliedDiscount}% off!</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Promo Code */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Promo code (optional)"
                    style={{
                      flex: 1,
                      padding: '0.6rem 0.75rem',
                      borderRadius: '6px',
                      border: `1px solid ${colors.grayDark}`,
                      background: colors.darkCard,
                      color: colors.white,
                      fontFamily: 'inherit',
                      fontSize: '0.85rem'
                    }}
                  />
                  <button
                    data-apply-promo
                    onClick={async () => {
                      if (!businessInfo.email || !businessInfo.email.includes('@')) {
                        setPromoError('Enter email first');
                        return;
                      }
                      if (!promoCode.trim()) return;
                      
                      const code = promoCode.trim().toUpperCase();
                      
                      // Client-side fallback for known promo codes
                      const validCodes = {
                        'TECHSHIELD100': 100,
                        'FRIEND100': 100,
                        'LAUNCH50': 50,
                        'EARLYBIRD': 25
                      };
                      
                      if (validCodes[code]) {
                        const discount = validCodes[code];
                        setAppliedDiscount(discount);
                        const newPrice = 29.99 * (1 - discount / 100);
                        setDiscountedPrice(newPrice);
                        setPromoError('');
                        
                        if (discount === 100) {
                          setPaymentComplete(true);
                          setShowPaywall(false);
                          generateReport();
                        }
                        return;
                      }
                      
                      // Try API validation
                      try {
                        const response = await fetch('/api/validate-promo', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ code: code })
                        });
                        const data = await response.json();
                        
                        if (data.valid) {
                          setAppliedDiscount(data.discount);
                          const newPrice = 29.99 * (1 - data.discount / 100);
                          setDiscountedPrice(newPrice);
                          setPromoError('');
                          
                          if (data.discount === 100) {
                            setPaymentComplete(true);
                            setShowPaywall(false);
                            generateReport();
                          }
                        } else {
                          setPromoError(data.message || 'Invalid code');
                          setAppliedDiscount(0);
                          setDiscountedPrice(29.99);
                        }
                      } catch (error) {
                        setPromoError('Invalid code');
                        setAppliedDiscount(0);
                        setDiscountedPrice(29.99);
                      }
                    }}
                    style={{
                      padding: '0.6rem 1rem',
                      borderRadius: '6px',
                      border: 'none',
                      background: colors.green,
                      color: colors.white,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '0.85rem'
                    }}
                  >
                    Apply
                  </button>
                </div>
                {promoError && (
                  <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.35rem' }}>{promoError}</p>
                )}
                {appliedDiscount > 0 && appliedDiscount < 100 && (
                  <p style={{ color: '#22c55e', fontSize: '0.75rem', marginTop: '0.35rem', fontWeight: 600 }}>
                    ✓ {appliedDiscount}% off! Pay ${discountedPrice.toFixed(2)}
                  </p>
                )}
              </div>

              {/* PayPal Button */}
              {businessInfo.email && businessInfo.email.includes('@') && appliedDiscount < 100 && (
                <div style={{ marginBottom: '1rem' }}>
                  {paypalLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: colors.gray }}>
                      <div style={{ marginBottom: '0.5rem' }}>⏳</div>
                      Loading payment options...
                    </div>
                  ) : (
                    <div ref={paypalRef} style={{ minHeight: '150px' }} />
                  )}
                </div>
              )}

              {(!businessInfo.email || !businessInfo.email.includes('@')) && (
                <p style={{ color: colors.gray, fontSize: '0.8rem', textAlign: 'center', marginTop: '0.5rem' }}>
                  Enter your email to see payment options
                </p>
              )}

              <p style={{ fontSize: '0.7rem', color: colors.grayDark, textAlign: 'center', marginTop: '1rem', lineHeight: 1.5 }}>
                This is educational guidance only, not an audit or legal advice.<br />
                <button onClick={() => setCurrentStep('terms')} style={{ background: 'none', border: 'none', color: colors.grayDark, textDecoration: 'underline', cursor: 'pointer', fontSize: '0.7rem' }}>Terms of Service</button>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Loading Screen
  if (loading) {
    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'center', paddingTop: '4rem' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `3px solid ${colors.darkCard}`,
            borderTopColor: colors.green,
            borderRadius: '50%',
            margin: '0 auto 1.5rem',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Generating Your Report
          </h2>
          <p style={{ color: colors.gray, fontSize: '0.9rem' }}>
            Analyzing your answers and creating personalized recommendations...
          </p>
          <p style={{ color: colors.grayDark, fontSize: '0.8rem', marginTop: '1rem' }}>
            This typically takes 15-20 seconds
          </p>
        </div>
      </div>
    );
  }

  // Report Screen
  if (currentStep === 'report' && report) {
    // Industry averages for comparison
    const industryAverages = {
      'Healthcare / Medical': 275,
      'Financial Services': 310,
      'Professional Services (Legal, Accounting, Consulting)': 295,
      'Religious Organization (Church, Synagogue, Mosque, Temple)': 245,
      'Education (Public School, District, University)': 260,
      'Private School / Academy': 270,
      'Nonprofit / Association': 255,
      'Retail / E-commerce': 280,
      'Construction (GCs, Builders, Developers)': 250,
      'Manufacturing / Warehouse': 265,
      'Restaurant / Hospitality': 240,
      'Real Estate': 275,
      'IT / Technology Services (Managed Services, Computer Repair, Web/Software)': 320,
      'Daycare / Childcare Center': 260,
      'Funeral Services (Funeral Home, Crematory, Cemetery)': 255,
      'Personal Services (Salon, Spa, Fitness, Pet Care)': 245,
      'Trades & Repair Services (HVAC, Plumbing, Auto, Electrical)': 250,
      'Other': 270
    };

    const industryContexts = {
      'Healthcare / Medical': 'Healthcare organizations face HIPAA documentation requirements with potential fines up to $50,000 per violation. Medical records must typically be retained 7+ years for adults, longer for minors.',
      'Financial Services': 'Financial firms face SEC/FINRA recordkeeping requirements with retention periods of 6+ years for many document types. Audit failures can result in regulatory sanctions.',
      'Professional Services (Legal, Accounting, Consulting)': 'Professional service firms must retain client files per state bar or board requirements, typically 7+ years. Malpractice claims often hinge on documentation.',
      'Religious Organization (Church, Synagogue, Mosque, Temple)': 'Religious organizations must maintain IRS 501(c)(3) documentation, donor records, and employment files. Average audit deficiency costs $15,000+ to remediate.',
      'Education (Public School, District, University)': 'Educational institutions face FERPA requirements for student records with specific retention and access rules. Title IX documentation is increasingly scrutinized.',
      'Private School / Academy': 'Private schools must maintain accreditation documentation, student records, and faculty credentials. Incomplete records can jeopardize accreditation status.',
      'Nonprofit / Association': 'Nonprofits must maintain IRS Form 990 supporting documentation for at least 3 years. Grant compliance records often require 7+ year retention.',
      'Retail / E-commerce': 'Retail businesses must maintain sales tax records, inventory documentation, and PCI compliance records if accepting cards. Audit penalties average $10,000+.',
      'Construction (GCs, Builders, Developers)': 'Construction firms must maintain contract documentation, change orders, and safety records. Missing lien waivers can result in double payment situations.',
      'Manufacturing / Warehouse': 'Manufacturing operations require quality control records, OSHA documentation, and supplier certifications. FDA-regulated facilities have additional requirements.',
      'Restaurant / Hospitality': 'Food service businesses must maintain health inspection records, food safety training, and tip reporting documentation. Alcohol licensing requires careful recordkeeping.',
      'Real Estate': 'Real estate transactions require meticulous documentation including agency disclosures, earnest money handling, and trust account records per state requirements.',
      'IT / Technology Services (Managed Services, Computer Repair, Web/Software)': 'IT service providers should maintain service level agreements, change logs, and client data handling procedures. SOC compliance increasingly required.',
      'Daycare / Childcare Center': 'Childcare facilities must maintain licensing documentation, staff ratios, incident reports, and immunization records. State inspections require immediate document access.',
      'Funeral Services (Funeral Home, Crematory, Cemetery)': 'Funeral homes must comply with FTC Funeral Rule documentation, maintain vital records, and pre-need contract trust funds. State board audits are common.',
      'Personal Services (Salon, Spa, Fitness, Pet Care)': 'Personal service businesses should maintain client consent forms, health/allergy information, and professional licenses. HIPAA may apply to medical spas.',
      'Trades & Repair Services (HVAC, Plumbing, Auto, Electrical)': 'Trade businesses must maintain licensing records, warranty documentation, and vehicle logs. Contractor license boards conduct random audits.',
      'Other': 'Most businesses face common documentation requirements including employment records, tax documents, and contracts with standard retention periods.'
    };

    const industryAvg = industryAverages[businessInfo.industry] || 270;
    const industryContext = industryContexts[businessInfo.industry] || industryContexts['Other'];
    const allIndustriesAvg = 275;

    const downloadPdf = () => {
      setReportSaved(true);
      
      const reportWindow = window.open('', '_blank');
      if (!reportWindow) {
        alert('Please allow popups to download your report');
        return;
      }

      // Calculate category scores from answers
      const categoryScores = {};
      categories.forEach((cat, idx) => {
        let catScore = 0;
        for (let i = 0; i < 5; i++) {
          const qIdx = idx * 5 + i;
          catScore += (answers[qIdx] || 0);
        }
        categoryScores[cat.name] = catScore;
      });

      const htmlContent = '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>AuditReadyIQ Report - ' + escapeHtml(businessInfo.name) + '</title>' +
        '<link href="https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">' +
        '<style>' +
        '*{margin:0;padding:0;box-sizing:border-box;}' +
        'body{font-family:Merriweather,Georgia,serif;max-width:900px;margin:0 auto;padding:40px;background:#fff;color:#333;line-height:1.8;font-size:11pt;}' +
        '.download-bar{font-family:Inter,sans-serif;background:#10b981;color:white;padding:15px 20px;margin:-40px -40px 30px -40px;display:flex;justify-content:space-between;align-items:center;}' +
        '.download-bar button{background:white;color:#10b981;border:none;padding:10px 25px;border-radius:6px;font-weight:600;cursor:pointer;font-size:14px;font-family:Inter,sans-serif;}' +
        '.header{text-align:center;margin-bottom:30px;padding-bottom:25px;border-bottom:3px solid #10b981;}' +
        '.logo{font-family:Inter,sans-serif;font-size:32px;font-weight:bold;margin-bottom:5px;}' +
        '.logo span{color:#10b981;}' +
        'h2{font-family:Inter,sans-serif;color:#166534;font-size:16pt;margin:30px 0 15px;padding-bottom:8px;border-bottom:2px solid #10b981;text-align:center;}' +
        'h3{font-family:Inter,sans-serif;color:#333;font-size:13pt;margin:20px 0 10px;}' +
        '.score-section{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:25px 0;}' +
        '.score-box{background:#f0fdf4;border:3px solid #10b981;padding:25px;border-radius:12px;text-align:center;}' +
        '.score-number{font-family:Inter,sans-serif;font-size:42pt;font-weight:bold;color:#10b981;}' +
        '.score-label{font-size:12pt;color:#666;}' +
        '.risk-level{font-family:Inter,sans-serif;display:inline-block;background:#dc2626;color:white;padding:6px 16px;border-radius:20px;font-weight:600;margin-top:10px;font-size:11pt;}' +
        '.risk-level.elevated{background:#f59e0b;}' +
        '.risk-level.moderate{background:#3b82f6;}' +
        '.risk-level.strong{background:#10b981;}' +
        '.score-comparison{background:#f8fafc;border:2px solid #e2e8f0;padding:20px;border-radius:12px;}' +
        '.comparison-item{display:flex;justify-content:space-between;align-items:center;padding:8px 0;}' +
        '.comparison-label{color:#64748b;font-size:10pt;}' +
        '.comparison-value{font-family:Inter,sans-serif;font-weight:600;color:#334155;}' +
        '.comparison-bar{height:10px;background:#e2e8f0;border-radius:5px;margin-top:4px;overflow:hidden;}' +
        '.comparison-fill{height:100%;border-radius:5px;}' +
        '.category-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin:20px 0;}' +
        '.category-item{text-align:center;padding:15px 10px;border-radius:10px;background:#f8fafc;border:1px solid #e2e8f0;}' +
        '.category-score{font-family:Inter,sans-serif;font-size:20pt;font-weight:bold;}' +
        '.category-max{font-size:9pt;color:#94a3b8;}' +
        '.category-name{font-size:9pt;color:#64748b;margin-top:5px;}' +
        '.cat-good .category-score{color:#10b981;}' +
        '.cat-ok .category-score{color:#f59e0b;}' +
        '.cat-bad .category-score{color:#dc2626;}' +
        '.summary-table{display:flex;justify-content:center;gap:20px;margin:20px 0;flex-wrap:wrap;}' +
        '.summary-item{text-align:center;padding:15px 25px;border-radius:8px;min-width:110px;}' +
        '.summary-item.critical{background:#fee2e2;}' +
        '.summary-item.attention{background:#fef3c7;}' +
        '.summary-item.good{background:#d1fae5;}' +
        '.summary-item .num{font-family:Inter,sans-serif;font-size:24pt;font-weight:bold;}' +
        '.summary-item.critical .num{color:#dc2626;}' +
        '.summary-item.attention .num{color:#f59e0b;}' +
        '.summary-item.good .num{color:#10b981;}' +
        '.summary-item .label{font-size:9pt;color:#666;}' +
        '.cost-analysis{background:linear-gradient(135deg,#fef2f2 0%,#fee2e2 100%);border:2px solid #dc2626;border-radius:12px;padding:25px;margin:25px 0;}' +
        '.cost-analysis h3{color:#991b1b;margin:0 0 15px;text-align:center;}' +
        '.cost-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;}' +
        '.cost-box{background:white;padding:18px;border-radius:8px;text-align:center;}' +
        '.cost-box.risk{border:2px solid #dc2626;}' +
        '.cost-box.invest{border:2px solid #10b981;}' +
        '.cost-amount{font-family:Inter,sans-serif;font-size:24pt;font-weight:bold;}' +
        '.cost-box.risk .cost-amount{color:#dc2626;}' +
        '.cost-box.invest .cost-amount{color:#10b981;}' +
        '.cost-label{font-size:10pt;color:#666;margin-top:5px;}' +
        '.cost-detail{font-size:9pt;color:#888;margin-top:8px;}' +
        '.issue-card{background:#fafafa;border-radius:12px;padding:24px;margin:20px 0;border:2px solid #e5e5e5;}' +
        '.issue-card.critical{border-color:#dc2626;border-top:6px solid #dc2626;}' +
        '.issue-card.attention{border-color:#f59e0b;border-top:6px solid #f59e0b;}' +
        '.issue-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:15px;flex-wrap:wrap;gap:10px;}' +
        '.issue-title{font-family:Inter,sans-serif;font-weight:700;color:#333;font-size:14pt;}' +
        '.issue-meta{display:flex;gap:8px;align-items:center;}' +
        '.badge{font-family:Inter,sans-serif;padding:4px 12px;border-radius:12px;font-size:9pt;font-weight:600;}' +
        '.badge.critical{background:#fee2e2;color:#dc2626;}' +
        '.badge.attention{background:#fef3c7;color:#b45309;}' +
        '.badge.priority{background:#d1fae5;color:#10b981;}' +
        '.your-answer{background:#f1f5f9;padding:12px 16px;border-radius:8px;margin:12px 0;border-left:4px solid #94a3b8;}' +
        '.your-answer-label{font-family:Inter,sans-serif;font-size:9pt;color:#64748b;text-transform:uppercase;font-weight:600;}' +
        '.your-answer-text{color:#334155;font-style:italic;margin-top:4px;}' +
        '.issue-details{display:grid;grid-template-columns:1fr 1fr;gap:15px;margin-top:15px;}' +
        '.detail-box{padding:15px;border-radius:8px;}' +
        '.detail-box.risk{background:#fff5f5;border:1px solid #fecaca;}' +
        '.detail-box.fix{background:#f0fdf4;border:1px solid #bbf7d0;}' +
        '.detail-box.impact{background:#fefce8;border:1px solid #fef08a;}' +
        '.detail-box.effort{background:#f0f9ff;border:1px solid #bae6fd;}' +
        '.detail-label{font-family:Inter,sans-serif;font-weight:700;font-size:9pt;text-transform:uppercase;margin-bottom:6px;}' +
        '.detail-box.risk .detail-label{color:#dc2626;}' +
        '.detail-box.fix .detail-label{color:#16a34a;}' +
        '.detail-box.impact .detail-label{color:#ca8a04;}' +
        '.detail-box.effort .detail-label{color:#0369a1;}' +
        '.detail-text{color:#444;font-size:10.5pt;line-height:1.6;}' +
        '.good-section{background:linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%);border:2px solid #10b981;border-radius:12px;padding:25px;margin:25px 0;}' +
        '.good-section h3{font-family:Inter,sans-serif;color:#10b981;margin-bottom:15px;text-align:center;}' +
        '.good-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}' +
        '.good-item{display:flex;align-items:center;gap:10px;background:white;border:1px solid #10b981;border-radius:8px;padding:10px 14px;}' +
        '.good-item .check{color:#10b981;font-weight:bold;font-size:14pt;}' +
        '.good-text{font-size:10pt;color:#166534;flex:1;}' +
        '.good-value{font-family:Inter,sans-serif;font-size:9pt;color:#059669;font-weight:600;}' +
        '.action-plan{background:linear-gradient(135deg,#f0fdf4 0%,#bbf7d0 100%);border:2px solid #10b981;padding:25px;border-radius:12px;margin:25px 0;}' +
        '.action-plan h2{border:none;margin:0 0 20px;color:#166534;}' +
        '.action-week{background:white;border-radius:10px;padding:20px;margin:12px 0;box-shadow:0 2px 8px rgba(0,0,0,0.05);}' +
        '.action-week h4{font-family:Inter,sans-serif;color:#10b981;margin:0 0 12px;font-size:12pt;padding-bottom:8px;border-bottom:1px solid #bbf7d0;text-align:center;}' +
        '.action-item{display:flex;align-items:flex-start;gap:12px;padding:10px 0;border-bottom:1px solid #f1f5f9;}' +
        '.action-item:last-child{border-bottom:none;}' +
        '.action-checkbox{width:18px;height:18px;border:2px solid #10b981;border-radius:4px;flex-shrink:0;margin-top:2px;}' +
        '.action-content{flex:1;}' +
        '.action-task{color:#333;font-size:10.5pt;}' +
        '.action-meta{display:flex;gap:15px;margin-top:4px;}' +
        '.action-time{font-family:Inter,sans-serif;font-size:9pt;color:#10b981;font-weight:500;}' +
        '.action-owner{font-family:Inter,sans-serif;font-size:9pt;color:#64748b;}' +
        '.quick-ref{background:#1e293b;color:white;border-radius:12px;padding:25px;margin:25px 0;}' +
        '.quick-ref h3{font-family:Inter,sans-serif;color:#10b981;text-align:center;margin-bottom:15px;}' +
        '.quick-ref-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:15px;}' +
        '.quick-ref-item{background:#334155;padding:15px;border-radius:8px;text-align:center;}' +
        '.quick-ref-label{font-size:9pt;color:#94a3b8;margin-bottom:5px;}' +
        '.quick-ref-value{font-family:Inter,sans-serif;font-size:14pt;font-weight:bold;color:#10b981;}' +
        '.share-box{background:#f0fdf4;border:2px solid #10b981;padding:20px;border-radius:10px;margin:20px 0;text-align:center;}' +
        '.share-box h3{font-family:Inter,sans-serif;color:#166534;margin-bottom:12px;}' +
        '.share-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:15px;}' +
        '.share-item{background:white;padding:12px;border-radius:8px;border:1px solid #bbf7d0;text-align:center;}' +
        '.share-role{font-family:Inter,sans-serif;font-weight:600;color:#166534;font-size:10pt;}' +
        '.share-why{font-size:9pt;color:#64748b;margin-top:4px;}' +
        '.resources{background:#f0fdf4;border:2px solid #10b981;padding:20px;border-radius:10px;margin:20px 0;text-align:center;}' +
        '.resources h3{font-family:Inter,sans-serif;color:#166534;margin-bottom:12px;}' +
        '.res-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;text-align:left;}' +
        '.res-item{background:white;padding:14px;border-radius:8px;border:1px solid #bbf7d0;}' +
        '.res-item a{color:#10b981;text-decoration:none;font-weight:600;font-size:11pt;}' +
        '.res-item p{margin:6px 0 0;font-size:9pt;color:#64748b;}' +
        '.disclaimer{background:#f5f5f5;padding:12px 15px;border-radius:8px;font-size:9pt;color:#666;margin:20px 0;text-align:center;}' +
        '.sources{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:15px;margin:20px 0;font-size:9pt;color:#64748b;}' +
        '.footer{text-align:center;margin-top:30px;padding-top:20px;border-top:2px solid #10b981;color:#888;font-size:9pt;}' +
        '.footer strong{color:#10b981;}' +
        '@media print{.download-bar{display:none !important;}}' +
        '.what-this-is h4 { color: #475569; margin: 0 0 8px; font-size: 13px; }' +
        '.what-this-is p { color: #64748b; margin: 0; font-size: 12px; line-height: 1.6; }' +
        '.section { margin: 35px 0; }' +
        '.section h2 { color: #10b981; font-size: 20px; font-weight: 700; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #d1fae5; }' +
        '.priority-item { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin: 15px 0; }' +
        '.priority-item h3 { color: #1f2937; margin: 0 0 8px; font-size: 16px; }' +
        '.priority-item p { color: #6b7280; margin: 0; font-size: 14px; line-height: 1.6; }' +
        '.issue { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin: 15px 0; }' +
        '.issue-critical { border-left: 4px solid #ef4444; }' +
        '.issue-attention { border-left: 4px solid #f59e0b; }' +
        '.issue-good { border-left: 4px solid #10b981; background: #f0fdf4; }' +
        '.issue-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }' +
        '.issue h3 { color: #1f2937; margin: 0; font-size: 16px; flex: 1; }' +
        '.badge { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; }' +
        '.badge-critical { background: #fee2e2; color: #dc2626; }' +
        '.badge-attention { background: #fef3c7; color: #d97706; }' +
        '.badge-fix { background: #dbeafe; color: #2563eb; }' +
        '.issue .answer-box { background: #f8fafc; border-left: 3px solid #cbd5e1; padding: 10px 15px; margin: 12px 0; border-radius: 0 8px 8px 0; }' +
        '.issue .answer-box .label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 3px; }' +
        '.issue .answer-box .text { color: #475569; font-size: 13px; font-style: italic; }' +
        '.issue .why-matters { margin: 12px 0; }' +
        '.issue .why-matters .label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 5px; display: flex; align-items: center; gap: 5px; }' +
        '.issue .why-matters p { color: #4b5563; font-size: 13px; line-height: 1.6; margin: 0; }' +
        '.issue .impact-box { background: #fef2f2; border-radius: 8px; padding: 12px 15px; margin: 12px 0; }' +
        '.issue .impact-box .label { font-size: 11px; color: #991b1b; text-transform: uppercase; margin-bottom: 3px; }' +
        '.issue .impact-box .value { font-size: 18px; font-weight: 700; color: #dc2626; }' +
        '.issue .impact-box .note { font-size: 11px; color: #7f1d1d; margin-top: 3px; }' +
        '.issue .suggestion { background: #f0fdf4; border-radius: 8px; padding: 15px; margin: 12px 0; }' +
        '.issue .suggestion .label { font-size: 11px; color: #166534; text-transform: uppercase; margin-bottom: 8px; display: flex; align-items: center; gap: 5px; }' +
        '.issue .suggestion p { color: #166534; font-size: 13px; line-height: 1.6; margin: 0; }' +
        '.issue .effort { display: flex; gap: 20px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e2e8f0; }' +
        '.issue .effort-item { font-size: 12px; color: #64748b; }' +
        '.issue .effort-item span { color: #10b981; font-weight: 600; }' +
        '.good-practices { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }' +
        '.good-item { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px 15px; display: flex; align-items: center; gap: 10px; }' +
        '.good-item .check { color: #10b981; font-weight: bold; }' +
        '.good-item .text { color: #166534; font-size: 13px; flex: 1; }' +
        '.good-item .points { color: #10b981; font-weight: 600; font-size: 13px; }' +
        '.action-plan { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); border-radius: 16px; padding: 25px; border: 1px solid #bbf7d0; }' +
        '.action-plan h2 { color: #166534; margin: 0 0 20px; font-size: 20px; text-align: center; }' +
        '.action-week { background: #fff; border-radius: 12px; padding: 20px; margin: 15px 0; }' +
        '.action-week h4 { color: #10b981; margin: 0 0 15px; font-size: 15px; border-bottom: 1px solid #d1fae5; padding-bottom: 10px; }' +
        '.action-item { display: flex; align-items: flex-start; gap: 10px; margin: 10px 0; padding: 10px; background: #f8fafc; border-radius: 6px; }' +
        '.action-item .checkbox { width: 18px; height: 18px; border: 2px solid #cbd5e1; border-radius: 4px; flex-shrink: 0; margin-top: 2px; }' +
        '.action-item .content { flex: 1; }' +
        '.action-item .task { color: #374151; font-size: 13px; }' +
        '.action-item .meta { font-size: 11px; color: #10b981; margin-top: 3px; }' +
        '.quick-ref { background: #374151; border-radius: 12px; padding: 20px 25px; margin: 25px 0; color: #fff; }' +
        '.quick-ref h4 { color: #10b981; margin: 0 0 15px; font-size: 15px; text-align: center; }' +
        '.quick-ref-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }' +
        '.quick-ref-item { text-align: center; }' +
        '.quick-ref-item .qr-label { font-size: 11px; color: #9ca3af; margin-bottom: 3px; }' +
        '.quick-ref-item .qr-value { font-size: 16px; font-weight: 600; color: #10b981; }' +
        '.quick-ref-item .qr-note { font-size: 10px; color: #6b7280; }' +
        '.who-should-see { background: #fffbeb; border-radius: 12px; padding: 20px; margin: 25px 0; border: 1px solid #fde68a; }' +
        '.who-should-see h4 { color: #92400e; margin: 0 0 15px; font-size: 14px; }' +
        '.who-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }' +
        '.who-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #78350f; }' +
        '.who-item .icon { font-size: 16px; }' +
        '.reassess { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 12px; padding: 25px; margin: 25px 0; text-align: center; border: 1px solid #fbbf24; }' +
        '.reassess h4 { color: #92400e; margin: 0 0 5px; font-size: 16px; }' +
        '.reassess .date { font-size: 20px; font-weight: 700; color: #78350f; margin: 10px 0; }' +
        '.reassess .goal { font-size: 13px; color: #a16207; }' +
        '.help-box { background: #f0fdf4; border: 2px solid #10b981; border-radius: 12px; padding: 25px; margin: 25px 0; text-align: center; }' +
        '.help-box h4 { color: #166534; margin: 0 0 10px; font-size: 16px; }' +
        '.help-box p { color: #166534; margin: 0 0 15px; font-size: 14px; }' +
        '.help-box a { color: #10b981; font-weight: 600; text-decoration: none; }' +
        '.resources { background: #f1f5f9; border-radius: 12px; padding: 20px; margin: 25px 0; }' +
        '.resources h4 { color: #475569; margin: 0 0 15px; font-size: 14px; text-align: center; }' +
        '.resources-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }' +
        '.resource-item { background: #fff; border-radius: 8px; padding: 12px; text-align: center; border: 1px solid #e2e8f0; }' +
        '.resource-item a { color: #10b981; font-size: 12px; font-weight: 600; text-decoration: none; }' +
        '.disclaimer { background: #fffbeb; border: 1px solid #f59e0b; border-radius: 12px; padding: 20px; margin: 30px 0; }' +
        '.disclaimer h4 { color: #b45309; margin: 0 0 10px; font-size: 13px; }' +
        '.disclaimer p { color: #92400e; margin: 0; font-size: 12px; line-height: 1.7; }' +
        '.footer { text-align: center; margin-top: 40px; padding-top: 25px; border-top: 2px solid #e2e8f0; }' +
        '.footer .company { font-weight: 600; color: #374151; font-size: 15px; }' +
        '.footer .contact { color: #6b7280; font-size: 13px; margin: 5px 0; }' +
        '.footer .contact a { color: #10b981; text-decoration: none; }' +
        '.footer .copyright { color: #9ca3af; font-size: 12px; margin-top: 10px; }' +
        '@media print { body { padding: 20px; } .section { page-break-inside: avoid; } }' +
        '</style></head><body>' +
        
        '<div class="download-bar"><span>📄 Your AuditReadyIQ™ Report</span><button onclick="window.print()">🖨️ Save as PDF / Print</button></div>' +
        
        '<div class="header"><div class="logo">AuditReady<span>IQ</span>™</div>' +
        '<p style="color:#666;">Documentation Readiness Assessment</p>' +
        '<p style="font-size:10pt;color:#888;">Prepared for: <strong>' + escapeHtml(businessInfo.name) + '</strong> | ' + escapeHtml(businessInfo.industry) + ' | ' + new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) + '</p></div>' +
        
        '<p style="text-align:center;font-size:9pt;color:#666;margin-bottom:15px;font-style:italic;">Scores are for educational reference only and reflect self-reported answers, not verified assessments.</p>' +
        
        '<div class="score-section">' +
        '<div class="score-box"><div class="score-number">' + (report.score || 0) + '</div><div class="score-label">out of 500 points</div>' +
        '<div class="risk-level ' + (report.riskLevel === 'HIGH RISK' ? '' : report.riskLevel === 'ELEVATED RISK' ? 'elevated' : report.riskLevel === 'MODERATE' ? 'moderate' : 'strong') + '">' + (report.riskLevel || 'PENDING') + '</div></div>' +
        '<div class="score-comparison"><h3 style="font-family:Inter;font-size:11pt;color:#334155;margin-bottom:12px;text-align:center;">How You Compare</h3>' +
        '<div class="comparison-item"><span class="comparison-label">Your Score</span><span class="comparison-value" style="color:#10b981;">' + (report.score || 0) + '</span></div>' +
        '<div class="comparison-bar"><div class="comparison-fill" style="width:' + Math.min(100, Math.round((report.score || 0) / 5)) + '%;background:#10b981;"></div></div>' +
        '<div class="comparison-item" style="margin-top:12px;"><span class="comparison-label">' + escapeHtml(businessInfo.industry.split(' (')[0]) + ' Average</span><span class="comparison-value">' + industryAvg + '</span></div>' +
        '<div class="comparison-bar"><div class="comparison-fill" style="width:' + Math.min(100, Math.round(industryAvg / 5)) + '%;background:#94a3b8;"></div></div>' +
        '<div class="comparison-item" style="margin-top:12px;"><span class="comparison-label">All Industries Average</span><span class="comparison-value">' + allIndustriesAvg + '</span></div>' +
        '<div class="comparison-bar"><div class="comparison-fill" style="width:' + Math.min(100, Math.round(allIndustriesAvg / 5)) + '%;background:#94a3b8;"></div></div>' +
        '<p style="font-size:9pt;color:' + ((report.score || 0) >= industryAvg ? '#10b981' : '#dc2626') + ';text-align:center;margin-top:12px;font-weight:600;">' + ((report.score || 0) >= industryAvg ? '✓ Above average for your industry!' : '⚠ Below industry average — room for improvement') + '</p>' +
        '</div></div>' +
        
        '<h2>Score by Category</h2>' +
        '<div class="category-grid">' +
        categories.map((cat, idx) => {
          const catScore = categoryScores[cat.name] || 0;
          const catClass = catScore >= 70 ? 'cat-good' : catScore >= 50 ? 'cat-ok' : 'cat-bad';
          return '<div class="category-item ' + catClass + '"><div class="category-score">' + catScore + '</div><div class="category-max">/ 100</div><div class="category-name">' + escapeHtml(cat.name) + '</div></div>';
        }).join('') +
        '</div>' +
        
        '<div class="summary-table">' +
        '<div class="summary-item critical"><div class="num">' + (report.criticalCount || 0) + '</div><div class="label">Areas to Review</div></div>' +
        '<div class="summary-item attention"><div class="num">' + (report.attentionCount || 0) + '</div><div class="label">Worth Checking</div></div>' +
        '<div class="summary-item good"><div class="num">' + (report.goodCount || 0) + '</div><div class="label">Good Practices</div></div>' +
        '</div>' +
        
        '<div class="cost-analysis"><h3>💰 Risk vs. Investment Analysis</h3>' +
        '<div class="cost-grid">' +
        '<div class="cost-box risk"><div class="cost-amount">$15,000+</div><div class="cost-label">Industry-Reported Avg. Remediation*</div><div class="cost-detail">Audit deficiencies, missing documentation, compliance penalties</div></div>' +
        '<div class="cost-box invest"><div class="cost-amount">~$150</div><div class="cost-label">Est. Cost to Improve</div><div class="cost-detail">Filing supplies, policy templates, admin time</div></div>' +
        '</div>' +
        '<p style="text-align:center;margin-top:15px;font-size:10pt;color:#991b1b;">Addressing gaps early may help reduce exposure significantly</p>' +
        '<p style="text-align:center;font-size:8pt;color:#888;margin-top:8px;">*Based on industry audit studies. Actual costs vary significantly by situation.</p>' +
        '</div>' +
        
        '<h2>Executive Summary</h2>' +
        '<p style="text-align:center;max-width:700px;margin:0 auto 15px;">' + escapeHtml(report.executiveSummary || 'Assessment completed. Review the detailed findings below.') + '</p>' +
        
        '<div style="background:#f5f5f5;border:1px solid #e0e0e0;border-radius:8px;padding:14px 18px;margin:15px auto;max-width:700px;text-align:center;">' +
        '<p style="margin:0 0 4px;font-family:Inter;font-weight:600;color:#555;font-size:10pt;">What This Report Is — And Isn\'t</p>' +
        '<p style="margin:0;color:#666;font-size:9pt;">This is educational guidance to help identify potential areas for improvement. It is <strong>not</strong> a professional audit, compliance certification, or evidence of any violation. Scores reflect self-reported answers, not verified assessments.</p>' +
        '</div>' +
        
        '<h2>Top 3 Suggested Priorities</h2>' +
        (report.priorities || []).map((p, i) => 
          '<div style="background:#f0fdf4;border-left:4px solid #10b981;padding:18px;margin:12px auto;border-radius:0 8px 8px 0;max-width:700px;">' +
          '<h4 style="font-family:Inter;color:#10b981;margin-bottom:6px;text-align:center;">' + (i + 1) + '. ' + escapeHtml(p.title || '') + '</h4>' +
          '<p style="text-align:center;font-size:10.5pt;">' + escapeHtml(p.reason || '') + '</p></div>'
        ).join('') +
        
        '<h2>Areas Flagged for Review</h2>' +
        (report.criticalIssues || []).map((issue, idx) => 
          '<div class="issue-card critical">' +
          '<div class="issue-header"><div class="issue-title">' + escapeHtml(issue.topic || '') + '</div>' +
          '<div class="issue-meta"><span class="badge critical">CRITICAL</span>' + (idx === 0 ? '<span class="badge priority">🔥 FIX FIRST</span>' : '') + '</div></div>' +
          '<div class="your-answer"><div class="your-answer-label">Your Answer</div><div class="your-answer-text">"' + escapeHtml(issue.answer || '') + '"</div></div>' +
          '<div class="issue-details">' +
          '<div class="detail-box risk"><div class="detail-label">⚠️ Why It Matters</div><div class="detail-text">' + escapeHtml(issue.risk || '') + '</div></div>' +
          '<div class="detail-box impact"><div class="detail-label">💰 Potential Impact</div><div class="detail-text" style="font-family:Inter;font-size:14pt;font-weight:bold;color:#dc2626;">' + escapeHtml(issue.impact || 'Varies') + '</div><div class="detail-text">' + escapeHtml(issue.impactNote || 'Costs vary based on circumstances') + '</div></div>' +
          '<div class="detail-box fix"><div class="detail-label">✅ Suggested Steps</div><div class="detail-text">' + escapeHtml(issue.fix || '') + '</div></div>' +
          '<div class="detail-box effort"><div class="detail-label">⏱️ Effort</div><div class="detail-text"><strong>' + escapeHtml(issue.effort || '1-2 hours') + '</strong><br>' + escapeHtml(issue.cost || 'No cost required') + '</div></div>' +
          '</div></div>'
        ).join('') +
        
        '<h2>Items Worth Reviewing</h2>' +
        (report.attentionIssues || []).map(issue => 
          '<div class="issue-card attention">' +
          '<div class="issue-header"><div class="issue-title">' + escapeHtml(issue.topic || '') + '</div><span class="badge attention">ATTENTION</span></div>' +
          '<div class="your-answer"><div class="your-answer-label">Your Answer</div><div class="your-answer-text">"' + escapeHtml(issue.answer || '') + '"</div></div>' +
          '<div class="issue-details" style="grid-template-columns:1fr 1fr;">' +
          '<div class="detail-box fix"><div class="detail-label">✅ Suggestion</div><div class="detail-text">' + escapeHtml(issue.fix || '') + '</div></div>' +
          '<div class="detail-box effort"><div class="detail-label">⏱️ Effort</div><div class="detail-text">' + escapeHtml(issue.effort || '1-2 hours') + '</div></div>' +
          '</div></div>'
        ).join('') +
        
        '<div class="good-section"><h3>✓ Good Practices Already in Place (' + (report.goodCount || 0) + ')</h3>' +
        '<div class="good-grid">' +
        (report.goodPractices || []).map(practice => 
          '<div class="good-item"><span class="check">✓</span><span class="good-text">' + escapeHtml(practice) + '</span><span class="good-value">+20</span></div>'
        ).join('') +
        '</div></div>' +
        
        '<div class="action-plan"><h2>🎯 Suggested 30-Day Action Plan</h2>' +
        '<div class="action-week"><h4>⚡ Week 1: Quick Wins</h4>' +
        ((report.actionPlan?.week1 || ['Review critical issues', 'Gather existing documentation']).map(item => 
          '<div class="action-item"><div class="action-checkbox"></div><div class="action-content"><div class="action-task">' + escapeHtml(item) + '</div><div class="action-meta"><span class="action-time">⏱️ 1-2 hours</span><span class="action-owner">👤 Admin</span></div></div></div>'
        ).join('')) +
        '</div>' +
        '<div class="action-week"><h4>🔧 Weeks 2-4: Core Improvements</h4>' +
        ((report.actionPlan?.week2to4 || ['Address attention items', 'Create missing policies']).map(item => 
          '<div class="action-item"><div class="action-checkbox"></div><div class="action-content"><div class="action-task">' + escapeHtml(item) + '</div><div class="action-meta"><span class="action-time">⏱️ 2-4 hours</span><span class="action-owner">👤 Leadership</span></div></div></div>'
        ).join('')) +
        '</div>' +
        '<div class="action-week"><h4>🔄 Ongoing (Set Calendar Reminders)</h4>' +
        ((report.actionPlan?.ongoing || ['Quarterly file audits', 'Annual policy review']).map(item => 
          '<div class="action-item"><div class="action-checkbox"></div><div class="action-content"><div class="action-task">' + escapeHtml(item) + '</div><div class="action-meta"><span class="action-time">⏱️ Quarterly/Annually</span><span class="action-owner">👤 Admin</span></div></div></div>'
        ).join('')) +
        '</div></div>' +
        
        '<div class="quick-ref"><h3>📋 Quick Reference Card</h3>' +
        '<div class="quick-ref-grid">' +
        '<div class="quick-ref-item"><div class="quick-ref-label">Document Review</div><div class="quick-ref-value">Quarterly</div></div>' +
        '<div class="quick-ref-item"><div class="quick-ref-label">Policy Updates</div><div class="quick-ref-value">Annually</div></div>' +
        '<div class="quick-ref-item"><div class="quick-ref-label">Retention Audit</div><div class="quick-ref-value">Annually</div></div>' +
        '</div></div>' +
        
        '<div class="share-box"><h3>📤 Who Should See This Report</h3>' +
        '<div class="share-grid">' +
        '<div class="share-item"><div class="share-role">Accountant/CPA</div><div class="share-why">Audit preparation</div></div>' +
        '<div class="share-item"><div class="share-role">Attorney</div><div class="share-why">Compliance review</div></div>' +
        '<div class="share-item"><div class="share-role">Board/Leadership</div><div class="share-why">Documentation</div></div>' +
        '<div class="share-item"><div class="share-role">Insurance Agent</div><div class="share-why">Liability coverage</div></div>' +
        '</div></div>' +
        
        '<div class="resources"><h3>📚 Free Resources</h3>' +
        '<p style="font-size:10pt;color:#64748b;margin-bottom:15px;">Trusted government and nonprofit resources for documentation compliance:</p>' +
        '<div class="res-grid">' +
        '<div class="res-item"><a href="https://www.dol.gov/agencies/whd/posters">DOL Workplace Posters</a><p>Free federal and state workplace poster requirements for employers.</p></div>' +
        '<div class="res-item"><a href="https://www.irs.gov/businesses/small-businesses-self-employed/how-long-should-i-keep-records">IRS Record Retention</a><p>Official IRS guidance on business recordkeeping requirements.</p></div>' +
        '<div class="res-item"><a href="https://www.osha.gov/recordkeeping">OSHA Recordkeeping</a><p>Workplace safety documentation requirements and forms.</p></div>' +
        '<div class="res-item"><a href="https://www.uscis.gov/i-9">USCIS I-9 Central</a><p>Official I-9 forms, instructions, and compliance guidance.</p></div>' +
        '</div></div>' +
        
        '<div class="sources"><strong style="color:#475569;">Sources:</strong> Audit remediation costs: Industry regulatory studies. Retention requirements: IRS Publication 583, DOL recordkeeping rules, state-specific regulations. This guidance references common documentation standards but does not constitute compliance certification.</div>' +
        
        '<div class="disclaimer"><strong>Important Disclaimer:</strong> This AuditReadyIQ™ assessment provides educational guidance only, based on self-reported answers. It is <strong>not</strong> a professional audit, compliance certification, or legal/regulatory advice. This report does not guarantee audit readiness, compliance with any framework, or specific outcomes. Retention periods vary by state. Cost estimates are industry approximations. Always consult qualified professionals (CPAs, attorneys, HR consultants) for your specific needs.</div>' +
        
        '<div class="footer"><p><strong>TechShield KC LLC</strong></p><p>auditreadyiq.com | <a href="mailto:info@techshieldkc.com" style="color:#888;">info@techshieldkc.com</a> | Kansas City, MO</p><p style="margin-top:8px;">© ' + new Date().getFullYear() + ' AuditReadyIQ™</p></div>' +
        
        '</body></html>';

      reportWindow.document.write(htmlContent);
      reportWindow.document.close();
    };

    return (
      <div style={baseStyles}>
        <style>{globalStyles}</style>
        
        <div style={{ 
          maxWidth: '500px', 
          margin: '0 auto', 
          textAlign: 'center',
          paddingTop: '3rem'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            fontSize: '2.5rem',
            color: 'white'
          }}>
            ✓
          </div>
          
          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 700, 
            marginBottom: '0.5rem',
            color: colors.white
          }}>
            Your Report is Ready!
          </h1>
          
          <p style={{ 
            color: colors.grayLight, 
            fontSize: '1rem', 
            marginBottom: '0.5rem' 
          }}>
            {businessInfo.name}
          </p>

          {report && typeof report.score !== 'undefined' && (
            <div style={{
              background: colors.darkCard,
              borderRadius: '12px',
              padding: '1.5rem',
              margin: '1.5rem 0'
            }}>
              <div style={{ fontSize: '3rem', fontWeight: 700, color: colors.green }}>
                {report.score}
              </div>
              <div style={{ color: colors.gray, fontSize: '0.9rem' }}>out of 500</div>
              <div style={{ color: colors.gray, fontSize: '0.75rem', marginTop: '4px' }}>Typical small organizations score between 250–350</div>
              <div style={{ 
                display: 'inline-block',
                background: report.riskLevel === 'HIGH RISK' ? '#dc2626' : 
                           report.riskLevel === 'ELEVATED RISK' ? '#f59e0b' :
                           report.riskLevel === 'MODERATE' ? '#3b82f6' : '#10b981',
                color: 'white',
                padding: '0.4rem 1rem',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: 600,
                marginTop: '0.75rem'
              }}>
                {report.riskLevel === 'HIGH RISK' ? 'HIGH RISK — immediate attention recommended' :
                 report.riskLevel === 'ELEVATED RISK' ? 'ELEVATED RISK — improvements needed' :
                 report.riskLevel === 'MODERATE' ? 'MODERATE — manageable with focused improvements' :
                 report.riskLevel === 'STRONG' ? 'STRONG — maintain and monitor' : report.riskLevel}
              </div>
              <div style={{ 
                color: colors.gray, 
                fontSize: '0.75rem', 
                marginTop: '1rem',
                paddingTop: '0.75rem',
                borderTop: `1px solid ${colors.grayDark}33`
              }}>
                25 questions × 20 points = 500 max. Higher is better.
              </div>
            </div>
          )}
          
          <button
            data-download-pdf="true"
            onClick={downloadPdf}
            style={{
              background: colors.green,
              border: 'none',
              borderRadius: '10px',
              padding: '1rem 2.5rem',
              color: colors.white,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '1.1rem',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              boxShadow: `0 4px 20px ${colors.green}40`,
              marginBottom: '2rem'
            }}
          >
            📄 Download PDF Report
          </button>
          
          <div style={{
            background: colors.greenLight,
            border: `1px solid ${colors.greenBorder}`,
            borderRadius: '10px',
            padding: '1.25rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ fontWeight: 600, color: colors.white, marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              Need help implementing recommendations?
            </p>
            <p style={{ color: colors.grayLight, fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              TechShield KC offers documentation consulting for small businesses and organizations.
            </p>
            <a 
              href="mailto:info@techshieldkc.com?subject=AuditReadyIQ%20Follow-up"
              style={{ color: colors.green, fontSize: '0.85rem', textDecoration: 'none', fontWeight: 500 }}
            >
              Contact us →
            </a>
          </div>
          
          <button
            onClick={() => {
              setCurrentStep('intro');
              setCurrentCategory(0);
              setAnswers({});
              setReport(null);
              setShowPaywall(false);
              setPaymentComplete(false);
            }}
            style={{
              background: 'transparent',
              border: `1px solid ${colors.grayDark}`,
              borderRadius: '8px',
              padding: '0.75rem 1.25rem',
              color: colors.grayLight,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: '0.85rem'
            }}
          >
            Start New Assessment
          </button>
          
          <div style={{ borderTop: `1px solid ${colors.grayDark}33`, paddingTop: '1.5rem', marginTop: '2rem' }}>
            <p style={{ fontSize: '0.7rem', color: colors.grayDark, lineHeight: 1.6 }}>
              This is educational guidance only, not a professional audit or compliance certification.
            </p>
            <p style={{ fontSize: '0.75rem', color: colors.gray, marginTop: '0.75rem' }}>
              © {new Date().getFullYear()} AuditReadyIQ™ by TechShield KC LLC
            </p>
            <p style={{ fontSize: '0.7rem', marginTop: '0.5rem' }}>
              <button onClick={() => setCurrentStep('privacy')} style={{ background: 'none', border: 'none', color: colors.grayDark, textDecoration: 'none', marginRight: '1rem', cursor: 'pointer' }}>Privacy Policy</button>
              <button onClick={() => setCurrentStep('terms')} style={{ background: 'none', border: 'none', color: colors.grayDark, textDecoration: 'none', cursor: 'pointer' }}>Terms of Service</button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuditReadyIQ;
